/**
 * El motor de HumanInk.
 *
 * No hay servidor propio del Studio: el motor es el mismo `companion` que ya sirve el panel de
 * Word, corriendo en el equipo del autor. Por eso el manuscrito y la clave de OpenRouter no salen
 * de su ordenador — aquí solo se leen y se pintan.
 *
 * En desarrollo las llamadas van por el proxy de Vite (/api → https://localhost:3001); cuando el
 * motor sirva el Studio, comparten origen y `BASE` queda vacío.
 */
/**
 * Dónde vive el motor.
 *
 * Tres situaciones, y una sola regla: el motor SIEMPRE corre en el ordenador del autor. Lo único
 * que cambia es desde dónde se ha cargado esta página.
 *
 *  · desarrollo        → el proxy de Vite, que evita CORS y el certificado local
 *  · servido por el motor → mismo origen, no hay nada que decir
 *  · alojado en studio.humanink.io → hay que nombrar el motor local explícitamente
 *
 * El tercer caso funciona porque el motor lo permite: su lista de orígenes incluye este dominio y
 * responde con `Access-Control-Allow-Private-Network`, que es lo que los navegadores exigen para
 * dejar que una web pública hable con un servidor de tu propia máquina. Es el mismo camino que el
 * panel de Word lleva usando desde word.humanink.io.
 */
import { claveIdempotencia, textoDeError } from "./logica";
import type { Consulta, DecisionDeVersion, GeneroLeido, Mostrado, PreferenciasEdicion, Propuesta } from "./logica";
const LOCAL = ["localhost", "127.0.0.1", "[::1]"];

const BASE = import.meta.env.DEV
  ? "/api"
  : LOCAL.includes(location.hostname) ? "" : "https://localhost:3001";

/**
 * El testigo de la sesión.
 *
 * En una CABECERA y no en una cookie, a propósito: el navegador manda las cookies solo en
 * cualquier petición que se le pida desde cualquier sitio, pero una web ajena no puede ponerle
 * cabeceras. Es lo que impide que una página abierta en otra pestaña escriba en tu manuscrito.
 */
let testigo: string | null = localStorage.getItem("hi.sesion");

export const hayTestigo = () => !!testigo;

export function guardarTestigo(t: string | null) {
  testigo = t;
  if (t) localStorage.setItem("hi.sesion", t);
  else localStorage.removeItem("hi.sesion");
}

/** Las cabeceras de cualquier llamada al motor. */
export function cabeceras(extra: Record<string, string> = {}): Record<string, string> {
  return { ...(testigo ? { authorization: `Bearer ${testigo}` } : {}), "x-hi-cliente": "studio", ...extra };
}

export class SinSesion extends Error {}

async function pedir<T>(ruta: string): Promise<T> {
  const r = await fetch(BASE + ruta, { headers: cabeceras() });
  if (r.status === 401) throw new SinSesion("El panel está cerrado.");
  if (!r.ok) throw new Error(`${ruta} respondió ${r.status}`);
  return (await r.json()) as T;
}

// ── La sesión ───────────────────────────────────────────────────────────────
/** `cuentas` solo lo dice un motor de Cuentas III en adelante; uno anterior sigue con contraseña. */
export interface EstadoSesion { cerrado: boolean; dentro: boolean; cuentas?: boolean; cuenta?: boolean; password?: boolean }
export const estadoSesion = () => pedir<EstadoSesion>("/sesion");

/** Un no del motor al entrar con la cuenta, con su código (`otra_cuenta`, `migrar_password`…). */
export class FalloCuenta extends Error {
  constructor(mensaje: string, public codigo: string) { super(mensaje); }
}

/**
 * Entrar con la cuenta (Cuentas III). El token de Clerk viaja al motor, el motor se lo enseña al
 * worker —que es quien lo verifica— y, si vale, abre la sesión local de siempre. El token de Clerk
 * NO se guarda en ningún sitio: dura un minuto y Clerk da otro cuando hace falta. Lo que se guarda
 * es el testigo del motor, como antes.
 */
export async function entrarConCuenta(clerk: string, password?: string) {
  const r = await fetch(BASE + "/sesion", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ clerk, ...(password ? { password } : {}) }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new FalloCuenta(d.error || "No se ha podido comprobar tu cuenta.", d.codigo || `http_${r.status}`);
  guardarTestigo(d.testigo);
  return d as { ok: true; testigo: string; caduca: number; email: string; sinRed: boolean; enlace: string | null };
}

export async function entrar(password: string) {
  const r = await fetch(BASE + "/sesion", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ password }),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "No se ha podido entrar.");
  guardarTestigo(d.testigo);
  return d as { ok: true; testigo: string; caduca: number };
}

export async function ponerPassword(nueva: string, anterior?: string) {
  const r = await fetch(BASE + "/sesion", {
    method: "POST", headers: { "content-type": "application/json", ...cabeceras() },
    body: JSON.stringify({ nueva, anterior }),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "No se ha podido guardar la contraseña.");
  guardarTestigo(d.testigo);
  return d;
}

/** Cerrar la sesión del motor. Con `olvidar`, además, este ordenador deja de estar atado a tu cuenta. */
export async function salir(olvidar = false) {
  await fetch(BASE + "/sesion" + (olvidar ? "?olvidar=1" : ""), { method: "DELETE", headers: cabeceras() }).catch(() => {});
  guardarTestigo(null);
}

export interface Colaborador {
  id: string;
  name: string;
  phase: string;
  output: "rewrite" | "document" | "report";
  goal: string;
}

export interface Uso {
  calls: number;
  errors: number;
  tokens: number;
  cost: number;
  ms: number;
}

export interface Telemetria {
  total: Uso;
  today: Uso;
  byAgent: Record<string, Uso>;
}

export interface Salud {
  ok: boolean;
  hasKey: boolean;
  version: string;
}

/** Un fichero del proyecto, tal y como lo ve el motor en el disco del autor. */
export interface Fichero { path: string; name: string; ext: string; size: number }

export const salud = () => pedir<Salud>("/health");

/**
 * Los ficheros del libro.
 *
 * El motor NO recuerda cuál es el manuscrito activo: lo lleva el cliente. Por eso el Studio es
 * el dueño de esa idea y la guarda. Sin ella, un entregable acaba en el Escritorio en vez de en
 * la carpeta del manuscrito — pasó la primera noche y es justo lo que hay que impedir.
 */
/** El texto de un documento del manuscrito. Lo extrae el motor: .md, .txt y .docx. */
// Un fichero que ya no está (renombrado, movido, borrado) se dice con palabras: el autor veía
// «/file?path=%2FUsers%2F… respondió 400» (21-sep).
export const documento = (ruta: string) =>
  pedir<{ path: string; text: string; chars: number } | { error: string }>(
    `/file?path=${encodeURIComponent(ruta)}`,
  ).catch((e) => {
    if (e instanceof SinSesion) throw e;
    const nombre = String(ruta).split(/[\\/]/).pop();
    throw new Error(`No encuentro «${nombre}» en la carpeta del proyecto. Si lo has renombrado o movido, elígelo otra vez arriba.`);
  });

/**
 * Subir un documento al manuscrito.
 *
 * Se guarda en la carpeta del manuscrito y a partir de ahí es un documento más del proyecto. No
 * existe la figura del «adjunto suelto»: si merece la pena adjuntarlo, merece la pena que viva
 * junto al libro.
 */
/**
 * Lo que devuelve una subida. Desde el 13-sep el motor NUNCA escribe encima: si ya había un
 * documento con ese nombre y contenido distinto, guarda el nuevo como `nombre-2` y deja el otro
 * intacto (`renombrado`); si era idéntico, no duplica nada (`igual`). La pantalla tiene que
 * decirlo, o el autor buscará «manuscrito.docx» y abrirá el de antes sin saber por qué.
 */
export interface Subida extends Fichero { pedido: string; renombrado: boolean; igual: boolean }

export async function subir(dir: string, fichero: File, nombre?: string): Promise<Subida> {
  const bytes = new Uint8Array(await fichero.arrayBuffer());
  let bin = "";
  for (let i = 0; i < bytes.length; i += 8192) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 8192));   // por trozos: evita reventar la pila
  }
  const r = await fetch(BASE + "/upload", {
    method: "POST",
    headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ dir, name: nombre ?? fichero.name, base64: btoa(bin) }),
  });
  const d = (await r.json()) as {
    path?: string; name?: string; size?: number; error?: string; pedido?: string; renombrado?: boolean; igual?: boolean;
  };
  if (!r.ok || d.error) throw new Error(textoDeError(d) || "No se pudo guardar el documento.");
  return {
    path: d.path!, name: d.name!, ext: d.name!.slice(d.name!.lastIndexOf(".")), size: d.size!,
    // Un motor anterior a la 1.6.0 no trae estos campos: se leen como «ni renombrado ni igual».
    pedido: d.pedido ?? fichero.name, renombrado: !!d.renombrado, igual: !!d.igual,
  };
}

export const proyecto = (dir: string) =>
  pedir<{ root: string; files: Fichero[]; existe?: boolean; carpeta?: boolean } | { error: string }>(
    `/project?dir=${encodeURIComponent(dir)}`,
  );
export const colaboradores = () => pedir<{ agents: Colaborador[] }>("/agents").then((d) => d.agents);
export const telemetria = () => pedir<Telemetria>("/telemetry");

/**
 * El dinero del consumo, como lo escribe un español: coma decimal y el símbolo detrás.
 *
 * Y en DÓLARES, que es en lo que factura OpenRouter. Ponerle un € a una cifra que viene en
 * dólares es contarle al autor un 8 % menos de lo que gasta, y una cifra de dinero mal etiquetada
 * se lleva por delante la confianza en todas las demás cifras del panel.
 */
export const dolares = (n: number) =>
  n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " $";

export const miles = (n: number) => n.toLocaleString("es-ES");

/** La respuesta cruda de OpenRouter que devuelve el motor. */
interface RespuestaChat {
  choices?: { message?: { content?: string } }[];
  error?: string | { message?: string };
}

/** Lo que devuelve un encargo: texto para leer, o un documento guardado en disco. */
export type Resultado =
  | { clase: "texto"; texto: string; op: string | null }
  | { clase: "entregable"; ruta: string | null; caracteres: number; op: string | null };

async function fallo(r: Response): Promise<never> {
  const d = (await r.json().catch(() => ({}))) as RespuestaChat;
  const m = typeof d.error === "string" ? d.error : d.error?.message;
  // 402: no es un error, es la puerta del producto. Se distingue con su propio tipo para que la
  // pantalla pueda ofrecer suscribirse en vez de enseñar un mensaje rojo.
  if (r.status === 402) throw new SinSuscripcion("Esto necesita una suscripción activa.");
  throw new Error(m || `El motor respondió ${r.status}`);
}

/**
 * Un encargo a un colaborador.
 *
 * El prompt del colaborador NO viaja desde aquí: vive en el motor, que lo compone. El Studio solo
 * dice a quién, qué se le pide y sobre qué texto. Así el mismo colaborador se comporta igual en
 * Studio y en Word — que es justo lo que se rompió entre julio y agosto cuando había dos copias.
 *
 * Y el camino depende de lo que ese colaborador HAGA. El corrector devuelve un fragmento
 * reescrito; el perfil de autor produce un documento y se guarda en la carpeta del proyecto.
 * Mandarlo todo por el mismo sitio es lo que hace que un colaborador conteste «sección pendiente
 * de redacción»: se le pidió reescribir algo que él no reescribe.
 */
export async function encargar(
  c: Colaborador, instruccion: string, texto: string, carpeta: string, contexto: string[] = [],
  // El modelo de ESTE encargo, si el autor lo cambió en el chat. Sin él, el motor usa el del agente.
  modelo?: string | null,
): Promise<Resultado> {
  // `saveDir` hace que el entregable caiga en la carpeta del manuscrito y no donde toque.
  // `contextFiles` son los documentos adjuntos: el motor los lee y los inyecta como REFERENCIA,
  // con el encargo de no reescribirlos. Es la diferencia entre «corrige esto» y «corrige esto
  // teniendo delante la biblia»: los adjuntos informan, no se tocan.
  const pedido = {
    agent: c.id, instruction: instruccion, text: texto, saveDir: carpeta, contextFiles: contexto,
    // `open: false` — el entregable cae en la carpeta y se avisa en el chat; abrir Word de golpe es
    // sacar al autor de donde estaba trabajando (17-sep). Un motor anterior a la 1.6.6 lo ignora.
    open: false,
    ...(modelo ? { model: modelo } : {}),
  };
  // La clave de idempotencia va en el CUERPO y no en una cabecera: una cabecera propia tiene que
  // declararla el motor en CORS, los motores instalados no la declaran, y el navegador bloqueaba el
  // encargo entero desde la web publicada (14-sep-2026). Un motor viejo ignora el campo.
  const cuerpo = JSON.stringify({ ...pedido, idempotencia: await claveIdempotencia(JSON.stringify(pedido)) });
  // Con las cabeceras del Studio: `x-hi-cliente` es lo que hace que, sin suscripción, el motor
  // conteste con la puerta del Studio y no con la de Word — dos productos, dos mensajes.
  const cab = cabeceras({ "content-type": "application/json" });

  if (c.output === "rewrite") {
    const r = await fetch(BASE + "/chat", { method: "POST", headers: cab, body: cuerpo });
    if (!r.ok) await fallo(r);
    const d = (await r.json()) as RespuestaChat;
    const t = d.choices?.[0]?.message?.content;
    if (!t) throw new Error("El colaborador no devolvió nada. Vuelve a intentarlo.");
    // El id llega por cabecera —el cuerpo es la respuesta cruda de OpenRouter— y es lo que
    // convierte «me lo quedo» en un hecho registrado y no en una suposición.
    return { clase: "texto", texto: t, op: r.headers.get("x-hi-op") };
  }

  const r = await fetch(BASE + "/deliverable", { method: "POST", headers: cab, body: cuerpo });
  if (!r.ok) await fallo(r);
  const d = (await r.json()) as { savedPath?: string | null; chars?: number };
  return { clase: "entregable", ruta: d.savedPath ?? null, caracteres: d.chars ?? 0, op: r.headers.get("x-hi-op") };
}

/**
 * Conversar con un colaborador: la respuesta vuelve al chat, no a un documento. Es la ruta de
 * conversación con historial que ya usa el panel de Word, así que funciona con los motores
 * instalados; el motor guarda los dos turnos en el hilo del proyecto.
 */
export async function conversar(
  c: Colaborador, texto: string, carpeta: string, contexto: string[] = [], modelo?: string | null,
  turno = 0, libroEntero = false, doc: string | null = null, flujo = false,
): Promise<{ texto: string; consultas: Consulta[]; propuestas: Propuesta[] }> {
  // La clave incluye en qué turno del hilo va: decir «sí» dos veces en una conversación son dos
  // mensajes; reenviar el mismo «sí» tras una recarga, en el mismo punto del hilo, es uno.
  const idempotencia = await claveIdempotencia({ agente: c.id, carpeta, turno, texto, modelo: modelo ?? null, libroEntero });
  const r = await fetch(BASE + `/chat-session?agent=${encodeURIComponent(c.id)}&dir=${encodeURIComponent(carpeta)}`, {
    method: "POST",
    headers: cabeceras({ "content-type": "application/json" }),
    // `doc` (motor 1.6.24): el manuscrito abierto, para que el chat sepa en qué libro y versión está
    // aunque no vaya adjunto. Un motor anterior lo ignora.
    // `flujo` (motor 1.7): el Studio como flujo, donde el coach es el único interlocutor y puede proponer el
    // trabajo de cualquier oficio. Va en el cuerpo: un motor anterior lo ignora (una cabecera nueva la rechazaría).
    body: JSON.stringify({ text: texto, contextFiles: contexto, idempotencia, contextFull: libroEntero, ...(doc ? { doc } : {}), ...(modelo ? { model: modelo } : {}), ...(flujo ? { flujo: true } : {}) }),
  });
  if (!r.ok) await fallo(r);
  // `consultas` (motor 1.6.27, CH-4): lo que el colaborador miró del libro para contestar. Un motor anterior no lo manda.
  // `propuestas` (motor 1.6.28, CH-4 fase B): las fichas de encargo que deja el colaborador.
  const d = (await r.json()) as { reply?: string; consultas?: Consulta[]; propuestas?: Propuesta[] };
  if (!d.reply) throw new Error("El colaborador no devolvió nada. Vuelve a intentarlo.");
  return { texto: d.reply, consultas: Array.isArray(d.consultas) ? d.consultas : [], propuestas: Array.isArray(d.propuestas) ? d.propuestas : [] };
}

/** Lo que el autor hizo con una ficha: queda en el hilo, para verla igual al volver. No gasta. */
export const marcarPropuesta = (dir: string, agente: string, id: string, estado: "encargado" | "descartado", trabajo: string | null = null) =>
  conCuerpo<{ ok: boolean }>("/chat-propuesta", "POST", { dir, agent: agente, id, estado, trabajo });

/**
 * «Hazlo»: lanza el trabajo de la ficha por el MISMO camino que su pantalla (informes, plan, pasada,
 * corrección, encargos). Devuelve el trabajo en segundo plano, o null si el motor lo hizo del tirón.
 */
export async function lanzarPropuesta(p: Propuesta, dir: string, doc: string, { rehacer = false, libre = false }: { rehacer?: boolean; libre?: boolean } = {}): Promise<Trabajo | null> {
  if (p.accion === "informe_lectura") {
    const d = await documento(doc);
    if ("error" in d) throw new Error(textoDeError(d) || "El motor no ha podido contestar.");
    // Con los mismos documentos del proyecto que adjunta Diagnóstico (Etapa.tsx, pedirLectura).
    const pr = await proyecto(dir).catch(() => null);
    const otros = pr && !("error" in pr) ? pr.files.filter((f) => f.path !== doc).slice(0, 6).map((f) => f.path) : [];
    const r = await informeGlobal(d.text, dir, otros, doc);
    return "trabajo" in r ? r.trabajo : null;
  }
  // `rehacer` como en su pantalla: si ya existe, se pide otro de verdad y no vuelve el de antes.
  if (p.informe) return pedirInforme(p.informe, dir, doc, rehacer);
  // En el flujo, el plan que propone el coach es el mismo que prepara la pantalla del Plan: el libre.
  if (p.accion === "plan") return prepararPlan(dir, doc, undefined, false, { libre });
  if (p.accion === "pasada") return (await ejecutarPasada(dir, doc, p.pasada ?? null)).trabajo;
  if (p.accion === "fase") return (await ejecutarFase(dir, doc, p.fase ?? null)).trabajo;
  if (p.accion === "correccion") return (await corregirManuscrito(dir, doc)).trabajo;
  if (p.encargo) return pedirEncargo(p.encargo, dir, doc, {}, rehacer);
  throw new Error("Este encargo no se puede lanzar desde aquí: búscalo en su pantalla.");
}

/**
 * ALCANCE: los capítulos tal cual los ve el motor en el documento, para poder restringir un
 * encargo a uno solo en vez de al libro entero. El texto de cada capítulo viaja completo —el
 * mismo documento que ya salió del navegador al abrirlo— así que elegir uno no pide nada nuevo
 * al motor cuando llega el momento de encargar.
 */
export interface Capitulo { title: string; texto: string; palabras: number }
/**
 * Los capítulos del documento. `fiable`: si es false, no hay capítulos que valgan y `motivo` lo dice.
 * Con `dir` y `doc` (motor 1.6.7, M-03) manda lo que el autor guardó, y luego los estilos del .docx.
 */
export const capitulosDeTexto = (texto: string, dir?: string | null, doc?: string | null) =>
  fetch(BASE + "/capitulos", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ text: texto, ...(dir ? { dir } : {}), ...(doc ? { doc } : {}) }),
  }).then((r) => conError<{
    capitulos: Capitulo[]; total: number; fiable?: boolean; motivo?: string | null;
    fuente?: "autor" | "estilos" | "texto"; cortes?: Corte[] | null; guardado?: boolean;
    /** Motor 1.6.32: párrafos que parecen título de capítulo y no llevan estilo. */
    sin_estilo?: { i: number; texto: string }[];
  }>(r));

/** Un corte de capítulo: el párrafo donde empieza y su título. */
export interface Corte { i: number; titulo: string }

/** Los cortes guardados del libro, y guardarlos corregidos (M-03). */
export const cortesDelLibro = (dir: string) =>
  pedir<{ cortes: Corte[]; fuente: string | null; doc?: string | null }>(`/capitulos/cortes?dir=${encodeURIComponent(dir)}`);

export const guardarCortes = (dir: string, doc: string | null, cortes: Corte[]) =>
  conCuerpo<{ cortes: Corte[]; fuente: string }>("/capitulos/cortes", "PUT", { dir, doc, cortes });

// ── Ajustes del motor ───────────────────────────────────────────────────────
export interface Config { hasKey: boolean; keyHint: string | null; usageOptOut: boolean;
  /** `false`: este motor no manda ni recuento de uso ni avisos de error (apagado de momento, 4-oct). Sin el campo, un motor anterior: sí los manda. */
  telemetria?: boolean }

export const ajustes = () => pedir<Config>("/config");

/**
 * ¿Se ha quedado atrás el motor de este ordenador?
 *
 * La web del Studio se actualiza sola; el motor que corre en el equipo del autor, no. Hasta la
 * 1.5.5 nadie se lo decía: el aviso de versión vivía solo en el panel de Word, colgado de la
 * comprobación de licencia, así que quien entra por suscripción no recibía ninguno. En cuanto una
 * versión añade una ruta nueva, el panel llama a algo que su motor no tiene.
 */
export interface Version { version: string; ultima: string | null; url: string | null; notas: string | null }
export const ultimaVersion = () => pedir<Version>("/ultima");

/**
 * El nombre con el que saludar.
 *
 * Estaba a fuego —«Buenas tardes, Rais»— así que cualquier cliente que instalara veía el nombre
 * del autor del producto en su propio panel. Es lo primero que se lee al abrir.
 *
 * Se saca del email de la licencia, que es el único dato de identidad que tenemos y que NO viaja
 * a ninguna parte: vive en el disco del autor. Si no hay email, no se inventa un nombre: se
 * saluda sin él. «Buenas tardes, Usuario» es peor que «Buenas tardes».
 */
export function nombreDe(correo: string | null | undefined): string | null {
  const local = String(correo || "").split("@")[0].trim();
  if (!local) return null;
  // rais.busom → Rais · maria_jose → Maria · jsmith84 → Jsmith
  const primero = local.split(/[._-]+/).filter(Boolean)[0];
  if (!primero) return null;
  const limpio = primero.replace(/[0-9]+$/, "");
  if (limpio.length < 2) return null;
  return limpio[0].toUpperCase() + limpio.slice(1).toLowerCase();
}

/** Buenos días, buenas tardes o buenas noches — según su reloj, no según el mío. */
export function saludoDelDia(): string {
  const h = new Date().getHours();
  if (h < 6) return "Buenas noches";
  if (h < 14) return "Buenos días";
  if (h < 21) return "Buenas tardes";
  return "Buenas noches";
}

/** Compara «1.5.10» con «1.5.9» como números, no como texto: por orden alfabético 1.5.10 < 1.5.9. */
export function hayNueva(actual: string, ultima: string | null): boolean {
  if (!ultima) return false;
  const a = actual.split(".").map(Number), b = ultima.split(".").map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if ((b[i] || 0) > (a[i] || 0)) return true;
    if ((b[i] || 0) < (a[i] || 0)) return false;
  }
  return false;
}

/** Guarda la clave de OpenRouter en el equipo del autor. No sale de ahí ni pasa por nosotros. */
export async function guardarAjustes(c: { openrouter_key?: string; usageOptOut?: boolean }) {
  const r = await fetch(BASE + "/config", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify(c),
  });
  if (!r.ok) throw new Error("El motor no pudo guardar los ajustes.");
  return (await r.json()) as Config;
}

// ── El informe de lectura ───────────────────────────────────────────────────
/**
 * Lo que devuelve el lector profesional sobre la novela entera.
 *
 * Es el único sitio del producto donde hay datos de verdad sobre el LIBRO —no sobre el uso—: una
 * nota por pilar, el BPS, el mapa de capítulos y los problemas con su cita literal. Por eso la
 * sección se construye sobre esto y no sobre gráficos de relleno.
 */
export interface Issue {
  excerpt: string; problema: string; arreglo: string; severidad: "alta" | "media" | "baja";
}
export interface Capitulo {
  n: number; title: string; palabras: number;
  notas?: Record<string, number>; resumen?: string; issues?: Issue[];
}
export interface Informe {
  narrative: string;
  data: {
    /** Motor 1.6.7 (M-01): novela, relatos, ensayo, memorias, manual o guion. */
    tipo?: TipoLibro;
    /** Las seis dimensiones con que se juzga ESE tipo de obra (M-02). Los informes viejos no las traen. */
    dimensiones?: { id: string; nombre: string }[];
    /** Lo que el lector pensó señalar y no señaló, con su motivo (M-12). */
    descartados?: { que: string; por_que: string }[];
    /** Con qué versión de la rúbrica se hizo (M-13): dos informes solo se comparan si coinciden. */
    rubrica?: number;
    scores?: Record<string, number | null>;
    bps?: number | null;
    ppt?: number;
    bps_pilares?: Record<string, number> | null;
    issues?: Issue[];
    /** Los diez ejes del estilo tal como los juzgó el informe de lectura (Control los pinta en el radar). */
    estilo_ejes?: Record<string, { puntuacion: number | null; justificacion: string }> | null;
  } | null;
  savedPath: string | null;
  chapterCount: number;
  /** Cuándo se hizo (lo guarda el motor): con ello se sabe si una versión posterior lo dejó viejo. */
  at?: string;
  /** Qué versión se leyó: si hoy trabajas sobre otra, el informe habla de un texto anterior. */
  doc?: string;
  chapters?: Capitulo[];
}

/**
 * Analiza el manuscrito entero. Cuesta dinero del autor y tarda minutos: se pide una vez y el
 * resultado se guarda, para que volver a la pantalla no vuelva a pagarlo.
 */
export type TipoLibro = "novela" | "relatos" | "ensayo" | "memorias" | "manual" | "guion";

/**
 * El tipo que el autor dijo la última vez para este libro; null si nunca lo dijo. «sin_ruta» si el
 * motor es anterior a la 1.6.5 y no sabe leer un ensayo: la pantalla lo dice en vez de fingir que sí.
 */
export async function tipoDelLibro(dir: string): Promise<TipoLibro | null | "sin_ruta"> {
  try {
    const r = await fetch(BASE + `/libro?dir=${encodeURIComponent(dir)}`, { headers: cabeceras() });
    if (r.status === 404) return "sin_ruta";
    if (!r.ok) return null;
    return ((await r.json()) as { tipo?: TipoLibro }).tipo ?? null;
  } catch { return null; }
}

export async function informeGlobal(texto: string, carpeta: string, contexto: string[] = [], doc: string | null = null, tipo: TipoLibro | null = null) {
  // `open: false` — en Word el informe se abre solo porque el autor ya está en Word; aquí
  // abrirle Word de golpe es echarlo de la aplicación en la que estaba trabajando.
  // `enSegundoPlano` (motor 1.6.3): el motor contesta 202 con el trabajo y sigue aunque se cierre la
  // pestaña. Un motor anterior no lo conoce y contesta el informe entero, como siempre.
  // `doc` (R-19): con él, el motor guarda la nota de ESA versión para la curva de las pasadas.
  // `tipo` (17-sep): un ensayo no se juzga con la rúbrica de novela. Un motor anterior lo ignora.
  const cuerpoInforme = { text: texto, saveDir: carpeta, contextFiles: contexto, open: false, enSegundoPlano: true, doc, ...(tipo ? { tipo } : {}) };
  const r = await fetch(BASE + "/report-global", {
    method: "POST",
    headers: cabeceras({ "content-type": "application/json" }),
    // Es la más cara de las tres llamadas —hasta 41 capítulos encadenados—. La clave va en el
    // cuerpo por lo mismo que en `encargar`: como cabecera, CORS la bloqueaba en la web publicada.
    body: JSON.stringify({ ...cuerpoInforme, idempotencia: await claveIdempotencia(cuerpoInforme) }),
  });
  if (!r.ok) await fallo(r);
  if (r.status === 202) return (await r.json()) as { trabajo: Trabajo };
  return (await r.json()) as Informe;
}

/** Un trabajo que el motor hace en segundo plano (R-17). */
export interface Trabajo {
  id: string; tipo: string; titulo: string;
  estado: "en_curso" | "hecho" | "fallido" | "interrumpido";
  progreso: { hecho: number; total: number; fase?: string } | null;
  inicio: string; fin: string | null;
  resultado: Record<string, unknown> | null; error: string | null;
  /** Los pasos del trabajo, si los sabe de antemano (motor 1.6.15+). Un motor anterior no los manda. */
  pasos?: { titulo: string; estado: "hecho" | "en_curso" | "pendiente" | "fallido" | "interrumpido" }[] | null;
  /** Si al relanzarlo aprovecha lo ya pagado (motor 1.6.30+). Lo dice el motor que lo lanzó. */
  reanudable?: boolean;
}
/**
 * Los trabajos de la carpeta. `null` = no se ha podido preguntar (el motor se está reiniciando, no
 * hay red): quien llama conserva lo que sabía y reintenta. Antes era una lista vacía, y un reinicio
 * del motor borraba de la pantalla una corrección a medias sin decir nada (Rais, 24-sep).
 */
export const trabajos = (dir: string) =>
  pedir<{ trabajos: Trabajo[] }>(`/trabajos?dir=${encodeURIComponent(dir)}`).then((d) => d.trabajos ?? []).catch(() => null);

/**
 * El último informe de este manuscrito, leído del disco.
 *
 * El motor lo guarda junto al libro. Por eso sobrevive a una recarga, a cambiar de navegador y a
 * cambiar de ordenador: un análisis que el autor ha pagado no puede vivir en el `localStorage` de
 * la pestaña donde se pidió.
 */
export async function ultimoInforme(carpeta: string, doc?: string | null): Promise<Informe | null> {
  // `doc`: el informe es del LIBRO, no de la carpeta (T-43). Un motor anterior lo ignora.
  const q = new URLSearchParams(doc ? { dir: carpeta, doc } : { dir: carpeta });
  const r = await fetch(BASE + `/report-last?${q}`, { headers: cabeceras() });
  // 404 es «no hay informe»; lo demás es «no he podido leerlo», y son dos cosas distintas para el
  // autor: una se arregla pidiéndolo y la otra recargando (Rais, 23-sep: el Diagnóstico decía «sin
  // hacer» sobre un libro que tenía su informe en el disco).
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`No he podido leer el informe de este libro (${r.status}).`);
  return (await r.json()) as Informe;
}

// ── La clave del autor, comprobada de verdad ────────────────────────────────
/**
 * Con el modelo «tú pones tu clave», el momento de pegarla es el punto donde se pierde a la
 * gente. Y el peor fallo no es pegarla mal: es pegar una clave nueva y válida de una cuenta SIN
 * crédito. Es válida, la comprobación ingenua diría que sí, y luego falla el primer encargo con
 * un error de OpenRouter que no explica nada.
 */
export interface Clave {
  estado: "sin_clave" | "rechazada" | "buena" | "sin_red";
  etiqueta?: string | null;
  gastado?: number | null;
  gastadoMes?: number | null;
  tope?: number | null;
  queda?: number | null;
  sinCredito?: boolean;
}

export const clave = () => pedir<Clave>("/openrouter");

// ── La bitácora ─────────────────────────────────────────────────────────────
/**
 * Qué se pidió, qué se hizo y qué decidiste tú.
 *
 * El motor lo escribe junto al manuscrito, en el equipo del autor. Aquí solo se lee: es el
 * registro que sostiene el certificado, y por eso importa que se pueda mirar entero.
 */
export interface Operacion {
  tipo?: string;       // hitos de AWAP 2: project.create · version.seal · declaration.sign
  version?: string;    // la versión sellada, en los version.seal
  id: string; ts: string; ms?: number | null; op: string;
  agente?: string | null; modelo?: string | null; cliente?: string;
  estado: "permitido" | "rechazado" | "fallido" | "en_curso" | "colgada";
  razon?: string | null; doc?: string | null; padre?: string | null; hijas: number;
  entrada?: { texto?: string; instruccion?: string; chars?: number; palabras?: number };
  salida?: { texto?: string; chars?: number; palabras?: number; ruta?: string | null };
  coste?: { cost?: number; prompt_tokens?: number; completion_tokens?: number } | null;
  decision?: { decision: string; metodo: string; similitud: number | null; ts: string; propio?: boolean } | null;
  /** Lo que dice un hito que pasó: «de_vuelta» es una lectura de lo que decidiste en Word (R0-11). */
  accion?: string;
  meta?: Record<string, unknown>;
}

/** Si la bitácora no está pudiendo escribir, hay que decirlo: un hueco en la prueba se avisa. */
export interface SaludBitacora {
  escribiendo: boolean; fallos: number; desde: string | null; motivo: string | null; bytes: number;
}

export interface Resumen {
  operaciones: number; coste: number; tokens: number;
  porDia: Record<string, number>; porOp: Record<string, number>;
  porAgente: Record<string, { ops: number; coste: number }>;
  porEstado: Record<string, number>;
  decisiones: Record<string, number>;
  /** Las decisiones separadas por dónde las tomaste (motor 1.6.30, R0-11): el chat y los encargos, y Word. */
  porVia?: {
    chat: { aceptado: number; editado: number; descartado: number; sin_decidir: number };
    word: { aceptado: number; editado: number; descartado: number; texto_propio: number; lecturas: number };
  };
  salud: SaludBitacora;
  /** Cambios de las pasadas que esperan a que se lea lo que decidiste en Word (motor 1.6.23). */
  en_word?: { sin_leer: number; versiones: { build: string; pendientes: number; motivo: string | null }[] } | null;
}

export const bitacora = (dir: string, f: Record<string, string> = {}) =>
  pedir<{ ok: boolean; total: number; ops: Operacion[]; cursor: string | null; salud: SaludBitacora }>(
    `/bitacora?${new URLSearchParams({ dir, ...f })}`,
  );

export const bitacoraResumen = (dir: string, doc?: string | null) =>
  pedir<Resumen>(`/bitacora/resumen?${new URLSearchParams(doc ? { dir, doc } : { dir })}`);

/**
 * Lo que el autor hace con lo que se le propone.
 *
 * `explicita` porque lo dice él con un botón. Es la prueba más fuerte que existe, y la razón de
 * que estos tres botones estén debajo de cada respuesta y no escondidos en un menú.
 */
export async function decidir(
  ref: string, decision: "aceptado" | "editado" | "descartado", dir: string, doc?: string,
  ambitos?: Ambito[],
) {
  const r = await fetch(BASE + "/decision", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ ref, decision, metodo: "explicita", dir, doc, fondo_ambitos: ambitos }),
  });
  if (!r.ok) throw new Error("No se pudo registrar la decisión.");
  return (await r.json()) as { ok: boolean; id: string | null };
}

/**
 * LAS DECISIONES DE FONDO (AWAP 2 §5.2 w1): lo que cambia la tela de la historia, no su superficie.
 *
 * Es el componente que más pesa después del texto propio, y el único que no se puede deducir de
 * nada: nadie puede saber, mirando el texto, que mover el final al invierno siguiente fue una
 * decisión tuya. O la marcas, o no consta — y si no consta, ese peso no actúa.
 */
export type Ambito = "trama" | "personaje" | "punto_de_vista" | "cronologia" | "final" | "tema";
export const AMBITOS: { id: Ambito; et: string }[] = [
  { id: "trama", et: "Trama" },
  { id: "personaje", et: "Personaje" },
  { id: "punto_de_vista", et: "Punto de vista" },
  { id: "cronologia", et: "Cronología" },
  { id: "final", et: "Final" },
  { id: "tema", et: "Tema" },
];

/** Una decisión de fondo que no viene de ninguna propuesta: la tomaste tú y punto. */
export async function decisionDeFondo(c: { dir: string; doc?: string | null; ambitos: Ambito[]; nota?: string }) {
  const r = await fetch(BASE + "/decision/fondo", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify(c),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(textoDeError(d) || "No se pudo registrar la decisión.");
  return d as { ok: true; id: string; ambitos: Ambito[] };
}

// ── Escribir en el manuscrito ───────────────────────────────────────────────
/**
 * Lo que se va a escribir, antes de escribirlo.
 *
 * No es un diff genérico: son las intervenciones EXACTAS que el motor va a aplicar. Si la
 * pantalla enseñara una cosa y el motor escribiera otra, la vista previa sería decorado.
 */
export interface Intervencion {
  op: "reemplazar" | "insertar_despues" | "borrar_parrafo";
  parrafo: number;
  huella: string | null;
  buscar?: string;
  reemplazar?: string;
  texto?: string[];
  autor: string;
  motivo?: string;
}

export interface VistaPrevia {
  lote: Intervencion[];
  sin_ancla: { motivo: string; texto: string }[];
  resumen: { reemplazos: number; inserciones: number; borrados: number; palabras_delta: number };
  origen: string;
  salida: string;
  build: number;
  duplicados: number[];
  sha256: string;
}

export interface Aplicado {
  salida: string;
  build: number;
  aplicadas: number;
  autor: string;
  verificacion: { ok: boolean; checks: { check: string; ok: boolean; detalle?: string; grave?: boolean }[] };
  resumen: VistaPrevia["resumen"];
  sin_ancla: VistaPrevia["sin_ancla"];
}

async function conError<T>(r: Response): Promise<T> {
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(textoDeError(d) || `El motor respondió ${r.status}`);
    Object.assign(e, { codigo: (d as { codigo?: string }).codigo, datos: d });
    throw e;
  }
  return d as T;
}

export const vistaPrevia = (c: {
  dir: string; docPath: string; original: string; propuesta: string; autor: string; motivo?: string;
}) => fetch(BASE + "/manuscrito/vista-previa", {
  method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify(c),
}).then((r) => conError<VistaPrevia>(r));

/** Y aquí se escribe. Todo o nada: si algo falla, no queda ningún build a medias. */
export const aplicar = (c: {
  dir: string; docPath: string; original: string; propuesta: string; autor: string;
  motivo?: string; sha256?: string; ref?: string | null;
}) => fetch(BASE + "/manuscrito/aplicar", {
  method: "POST", headers: cabeceras({ "content-type": "application/json" }),
  body: JSON.stringify(c),
}).then((r) => conError<Aplicado>(r));

// ── Las notas del libro (M-09) ──────────────────────────────────────────────
/**
 * La NOTA EDITORIAL es la unidad de trabajo: lo que te han dicho del libro, con su sitio en el
 * manuscrito y su estado. El orden lo pone el autor, no la herramienta.
 */
export type EstadoNota =
  | "por_decidir" | "en_pasada" | "en_word" | "resuelta" | "reabierta" | "por_comprobar" | "luego" | "no_aplica";

export interface Nota {
  id: string;
  huella: string;
  nivel: "estructura" | "capitulo" | "linea";
  dimension: string | null;
  gravedad: "alta" | "media" | "baja";
  titulo: string;
  problema: string;
  arreglo: string;
  ancla: { capitulo: number | null; titulo_capitulo: string | null; parrafo: number | null; cita: string | null };
  fuentes: { informe: string; version: number | null; fecha: string }[];
  estado: EstadoNota;
  comentario: string | null;
  pasada: number | null;
  /** La nota de una fase entera (varias pasadas en una versión): su número. */
  fase?: number;
  /** En qué versión quedó escrito su cambio (la pasada lo anota): es la llave de su manifiesto. */
  build?: string | null;
  /** «pasada»: la escribió una pasada del plan con los cambios que no contestaban a ninguna nota (R0-6). */
  origen?: "pasada";
  /** Los capítulos de la pasada que la escribió. */
  capitulos?: number[];
  /** Cómo se cerró, leído de tu documento. */
  cierre?: { fecha: string; motivo: string; build: string | null; metodo: string } | null;
  /** Por qué ha vuelto a «por decidir»: rechazaste su cambio en Word (R0-6). */
  devuelta?: { fecha: string; motivo: string; build: string | null } | null;
  creada: string;
}

/** Las notas del libro, ya ordenadas por lo que más mueve la nota. Motor anterior a la 1.6.7: vacío. */
export const notasDelLibro = (dir: string, doc?: string | null) =>
  pedir<{ notas: Nota[]; sin_anclar?: number }>(`/notas?${new URLSearchParams(doc ? { dir, doc } : { dir })}`)
    .catch(() => ({ notas: [] as Nota[] }));

export const decidirNota = (dir: string, doc: string | null, id: string, d: { estado?: EstadoNota; comentario?: string | null; pasada?: number | null }) =>
  conCuerpo<{ nota: Nota }>(`/notas/${encodeURIComponent(id)}`, "PATCH", { dir, doc, ...d }).then((r) => r.nota);

/**
 * Llevar las notas al documento (M-27): un marcador de Word por nota —«Ir a → Marcador»— y, si se
 * piden, también comentarios. Escribe una COPIA: el manuscrito no se toca.
 */
export const notasAWord = (dir: string, doc: string, comentarios = false) =>
  conCuerpo<{ puestos: { id: string; marcador?: string; comentario?: string }[]; fallidos: { id: string; error: string }[]; documento: string }>(
    "/notas/a-word", "POST", { dir, doc, comentarios });

/**
 * De vuelta de Word (M-21): el motor lee el documento que guardaste y clasifica cada cambio por el
 * RESULTADO —aceptado, retocado por ti, rechazado— y cierra con prueba las notas cuyo pasaje cambió.
 *
 * Desde el motor 1.6.23 no hacen falta marcas (`metodo: "resultado"`): lee TODAS las versiones que
 * guardaste encima, con el manifiesto de cada pasada. `sin_efecto` es lo que la pasada quitaba y
 * volvía a poner igual: no es una decisión tuya y no cuenta.
 */
export interface VueltaLeida {
  metodo?: "resultado";
  entregada: string;
  total: { aceptadas: number; retocadas: number; rechazadas: number; no_evaluables: number; evaluadas: number; ratio: number | null; sin_efecto?: number; suyas?: number };
  cuenta: { resueltas: number; siguen: number; por_comprobar: number; devueltas?: number; descartadas?: number };
  notas: { id: string; estado: string; motivo: string }[];
  leidas?: { build: string }[];
}
export const deVueltaDeWord = (dir: string, doc: string) =>
  conCuerpo<VueltaLeida>("/notas/de-vuelta", "POST", { dir, doc });

/**
 * Lo que va a costar la pasada, ANTES de lanzarla (M-20).
 *
 * El motor cuenta las palabras que de verdad van a viajar —los capítulos de las notas elegidas,
 * troceados igual que en la pasada—, así que la cifra es la del encargo que se va a hacer y no una
 * estimación calculada por otro camino. `aviso` es la línea suave de M-20; nunca un candado.
 */
export const costeDeLaPasada = (dir: string, doc: string, notas: string[] | null = null, pasada: number | null = null) =>
  conCuerpo<{ palabras: number; llamadas: number; capitulos: { n: number; titulo: string }[]; aviso: string | null }>(
    "/pasada/coste", "POST", { dir, doc, notas, pasada });

/**
 * El manifiesto de una versión (M-19, M-28): las intervenciones que se escribieron de verdad, con
 * su nota y su porqué, y —si ya se ha leído de vuelta— lo que hiciste con cada una. Antes de abrir
 * Word sabes qué se ha cambiado por cada nota; en Word solo decides.
 *
 * Una versión anterior al motor 1.6.7 no lleva manifiesto: se devuelve `null` y la pantalla lo dice
 * con esas palabras, en vez de enseñar una lista vacía que parecería «no se cambió nada».
 */
export interface CambioDeVersion {
  n: number;
  op: string | null;
  parrafo: number | null;
  quita: string;
  pone: string;
  nota: string | null;
  /** Todas las notas que contesta (una operación puede contestar varias). */
  notas?: string[];
  por_que: string;
  resultado: "aceptado" | "retocado" | "rechazado" | "no_evaluable" | null;
}

export interface Manifiesto {
  build: string;
  origen: string | null;
  fecha: string;
  firma: string | null;
  pasada: number | null;
  cambios: CambioDeVersion[];
}

export const manifiestoDeVersion = (dir: string, doc: string | null, build: string) =>
  pedir<Manifiesto>(`/manifiesto?${new URLSearchParams(doc ? { dir, doc, build } : { dir, build })}`)
    .catch(() => null);

export const anadirNota = (dir: string, doc: string | null, n: { titulo: string; problema?: string; arreglo?: string; capitulo?: number | null; cita?: string; gravedad?: "alta" | "media" | "baja" }) =>
  conCuerpo<{ nota: Nota }>("/notas", "POST", { dir, doc, ...n }).then((r) => r.nota);

// ── La curva de la reescritura ──────────────────────────────────────────────
/**
 * Lo que el motor midió de cada versión del libro.
 *
 * Es la única serie del producto que no se puede reconstruir a posteriori: para tenerla hay que
 * haber medido desde la primera versión. Por eso `sinMedir` importa —decir «faltan 12» es más
 * honesto que enseñar media curva como si fuera entera.
 */
export interface Version {
  n: number;
  build: string | null;
  palabras: number;
  delta: number | null;
  marcas: number | null;
  autores: string[];
  metricas: Record<string, number> | null;
  nota: number | null;
  aceptadas: number | null;
  propuestas: number | null;
  /** Lo que hiciste con sus cambios, leído de vuelta de Word (motor 1.7). */
  decision?: DecisionDeVersion | null;
}

export interface Serie {
  versiones: Version[];
  global: { resumen?: Record<string, number> } | null;
  sinMedir: number;
  /** Motor 1.6.5: con `doc`, la serie es solo de su libro, y aquí qué otros libros hay en la carpeta. */
  libro?: string | null;
  otrosLibros?: string[];
}

export const pasadas = (dir: string, doc?: string | null) =>
  pedir<Serie>(`/pasadas?${new URLSearchParams(doc ? { dir, doc } : { dir })}`);

// ── Volver a una versión anterior (P-04, motor 1.6.32) ──────────────────────
export interface VersionDelLibro { nombre: string; ruta: string; v: number; fecha: string | null }
/** Las versiones de este libro en la carpeta, de la más nueva a la más vieja. null: el motor no sabe volver atrás. */
export const versionesDelLibro = (dir: string, doc: string) =>
  pedir<{ versiones: VersionDelLibro[] }>(`/versiones?${new URLSearchParams({ dir, doc })}`).then((d) => d.versiones).catch(() => null);
export interface PrevisionVolver {
  a: string; vA: number; ultima: string; vUltima: number; nueva: string; vNueva: number; entre: number;
  pasadas: { n: number; titulo: string | null; build: string }[];
  notas: { id: string; titulo: string | null; estado: string }[];
}
/** Qué pasaría al volver a `a`, sin tocar nada. Si no se puede, lanza con el motivo en palabras. */
export async function preverVolver(dir: string, doc: string, a: string): Promise<PrevisionVolver> {
  const r = await fetch(BASE + `/versiones/volver?${new URLSearchParams({ dir, doc, a })}`, { headers: cabeceras() });
  if (r.status === 401) throw new SinSesion("El panel está cerrado.");
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(textoDeError(d) || "No he podido preparar la vuelta a esa versión.");
  return d as PrevisionVolver;
}
/** Volver de verdad: el motor copia `a` como la siguiente versión y reabre el plan y las notas. */
export async function volverAVersion(dir: string, doc: string, a: string): Promise<{ nueva: string; vNueva: number; vA: number; pasadas: { n: number }[]; notas: number }> {
  const r = await fetch(BASE + "/versiones/volver", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ dir, doc, a }),
  });
  if (r.status === 401) throw new SinSesion("El panel está cerrado.");
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(textoDeError(d) || "No he podido volver a esa versión.");
  return d;
}

/** Mide la cadena del libro del documento. Devuelve enseguida lo que ya había: medir son minutos. */
export const medirPasadas = (dir: string, doc?: string | null) =>
  fetch(BASE + "/pasadas/medir", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify(doc ? { dir, doc } : { dir }),
  }).then((r) => r.json() as Promise<Serie & { midiendo: boolean }>);

// ── La suscripción ──────────────────────────────────────────────────────────
/**
 * Lo que hace falta para trabajar, atado a ESTA instalación.
 *
 * Sin suscripción se puede abrir el libro, mirarlo, leer la bitácora y ver las medidas: todo lo
 * que no le pide nada a un colaborador. Encargar y escribir en el manuscrito, no. Se enseña la
 * casa entera antes de pedir la tarjeta.
 */
export class SinSuscripcion extends Error {}

export interface Suscripcion {
  instalacion: string;
  activa: boolean;
  plan: string | null;
  hasta: number | null;
  enlaces: { mensual: string | null; anual: string | null } | null;
  estado: string | null;
  portal: string | null;
  licencia: { activa: boolean; tier?: string; perpetua?: boolean; correo?: string | null };
  sinRed: boolean;
}

export const suscripcion = () => pedir<Suscripcion>("/suscripcion");

/**
 * El selector de ficheros del sistema.
 *
 * Lo abre el motor, no el navegador: un `<input type="file">` entrega el contenido del fichero
 * pero NUNCA su ruta, y el motor trabaja sobre el .docx donde está, no sobre una copia. Es de las
 * pocas cosas que una aplicación local puede hacer y una web no.
 */
// ── Identidad: traer aquí lo que ya es tuyo ─────────────────────────────────────────────────
//
// No es un registro ni una cuenta. Es la respuesta a dos preguntas que el producto no sabía
// contestar: «me han regalado un año, ¿cómo lo cojo?» y «me he cambiado de portátil».
//
// El identificador de esta instalación no sale de aquí: lo pone el motor al hablar con el
// servidor. Desde el navegador solo viajan el email y el código.

/** Pide el código. La respuesta es siempre la misma tenga derecho o no — a propósito. */
export async function reclamar(email: string) {
  const r = await fetch(BASE + "/reclamar", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ email }),
  });
  if (!r.ok) await fallo(r);
  return (await r.json()) as { ok: boolean; mensaje?: string };
}

export interface Canje { ok: boolean; error?: string; plan?: string; hasta?: number; movida?: boolean; quedan?: number }

/** Canjea el código y ata esta instalación a lo que ese email tenga. */
export async function canjear(email: string, codigo: string): Promise<Canje> {
  const r = await fetch(BASE + "/canjear", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ email, codigo }),
  });
  if (!r.ok) await fallo(r);
  return (await r.json()) as Canje;
}

export async function elegirDelSistema(tipo: "carpeta" | "documento" = "carpeta", desde?: string) {
  const r = await fetch(BASE + "/elegir", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ tipo, desde }),
  });
  const d = await r.json();
  if (d.error) throw new Error(textoDeError(d) || "El motor no ha podido contestar.");
  return d as { ruta?: string; cancelado?: boolean };
}

// ── El explorador ───────────────────────────────────────────────────────────
export interface Carpeta { nombre: string; ruta: string; documentos: number }
export interface Listado {
  ruta: string; padre: string | null;
  migas: { nombre: string; ruta: string }[];
  carpetas: Carpeta[];
  documentos: { nombre: string; ruta: string }[];
  atajos: { nombre: string; ruta: string }[];
  error?: string;
}

export const carpetas = (dir?: string) =>
  pedir<Listado>(`/carpetas${dir ? `?${new URLSearchParams({ dir })}` : ""}`);

// ── Las conversaciones ──────────────────────────────────────────────────────
//
// Un colaborador es alguien con quien se tiene una conversación abierta, no una opción de un
// desplegable. Por eso el hilo se guarda en la carpeta del manuscrito y se lee de ahí: es del
// autor, viaja con su libro y sobrevive a cerrar el panel.
//
// Y es el MISMO hilo que usa el panel de Word. Si el corrector recuerda en el Studio lo que le
// dijiste en Word, es un equipo; si no, son dos formularios que comparten logotipo.

export interface Asomo {
  id: string;
  mensajes: number;
  ultimo: { mio: boolean; texto: string; at: number | null };
}

export const bandeja = (dir: string) =>
  pedir<{ conversaciones: Asomo[] }>(`/conversaciones?dir=${encodeURIComponent(dir)}`)
    .then((d) => d.conversaciones);

export interface Mensaje { role: "user" | "assistant"; content: string; at?: number; op?: string; consultas?: Consulta[]; propuestas?: Propuesta[] }

export const hilo = (agente: string, dir: string) =>
  pedir<{ history: Mensaje[] }>(
    `/chat-session?agent=${encodeURIComponent(agente)}&dir=${encodeURIComponent(dir)}`,
  ).then((d) => d.history ?? []);

/** Deja constancia de un turno ya ocurrido. Si falla, la conversación sigue: no se pierde nada
 *  de lo que se ve en pantalla, solo el recuerdo de la próxima vez — y eso se dice. */
export async function anexar(agente: string, dir: string, turnos: Mensaje[]) {
  const r = await fetch(BASE + "/hilo", {
    method: "POST",
    headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ agent: agente, dir, turnos }),
  });
  if (r.status === 401) throw new SinSesion("El panel está cerrado.");
  if (!r.ok) await fallo(r);
}

/** Borra la conversación con un colaborador. Destructivo y del autor: solo desde su botón. */
export async function olvidar(agente: string, dir: string) {
  const r = await fetch(
    BASE + `/chat-session?agent=${encodeURIComponent(agente)}&dir=${encodeURIComponent(dir)}`,
    { method: "DELETE", headers: cabeceras() },
  );
  if (!r.ok) await fallo(r);
}

/** «hace 4 min», «ayer», «12 ago». Lo que se lee de un vistazo en una lista de conversaciones. */
export function cuando(at: number | null): string {
  if (!at) return "";
  const min = Math.floor((Date.now() - at) / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `${min} min`;
  const hoy = new Date(); const d = new Date(at);
  const mismoDia = hoy.toDateString() === d.toDateString();
  if (mismoDia) return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  const ayer = new Date(hoy); ayer.setDate(hoy.getDate() - 1);
  if (ayer.toDateString() === d.toDateString()) return "ayer";
  if (hoy.getTime() - at < 7 * 864e5) return d.toLocaleDateString("es-ES", { weekday: "short" });
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

// ── AWAP 2: las dos vías y la posición ─────────────────────────────────────
/**
 * El certificado nuevo (especificación del 8-sep-2026). Dos ejes que NO se suman: la cobertura
 * —desde qué punto de la vida de la obra hay registro, fijada al entrar y nunca modificada— y el
 * HAS sobre la ventana registrada. Aquí solo viajan hashes, cifras y la declaración del autor.
 */
export type EntryPoint = "premisa" | "biblia" | "escaleta" | "borrador_parcial" | "manuscrito" | "fichero";
export type OpcionOrigen = "autor" | "tercero" | "asistido_ia" | "generado_ia" | "desconocido" | "no_declara";

export interface HasVentana {
  valor: number | null;
  banda: string | null;            // «60-69»: se publica en bandas de 10, nunca el entero
  has_version: string;             // los pesos, versionados: cambiarlos cambia la versión
  componentes: Record<string, number>;
  excluidos: string[];             // lo que no tenía datos y NO se imputó
  ventana: { desde: string | null; hasta: string | null; eventos: number; generaciones: number; sesiones: number; dias: number };
  decisiones: { total: number; con_decision: number; aceptadas: number; editadas: number; rechazadas: number };
}

export interface Escaneo {
  disponible?: boolean; motivo?: string;
  fichero?: string; tipo?: string; palabras?: number;
  invisibles?: Record<string, unknown>; homoglifos?: unknown[];
  metadatos?: Record<string, string>; imagenes?: unknown[];
  marca_de_texto?: { detectada: null | boolean; motivo: string };
}

export interface VersionSellada { id: string; ts: string; sha256: string; doc: string | null; palabras: number | null; autor_declarado: string | null }

export interface Posicion {
  proyecto: { id: string; titulo: string; autor: string; estado: "activo" | "pendiente_declaracion"; integridad: "intacta" | "rota" };
  cobertura: { valor: number; entry_point: EntryPoint; entry_ts: string; track: "origen" | "transformacion" };
  has: HasVentana | null;
  cuadrante: { id: "Q1" | "Q2" | "Q3" | "Q4"; nombre: string };
  // §12 (Rais, 11-sep): el eje Y se llama MAS en la vía B; la posición es privada; los pesos, a la vista.
  // Opcionales: un motor anterior no los manda.
  eje_y?: { sigla: "HAS" | "MAS"; nombre: string };
  publicacion?: "privada";
  pesos?: Record<string, number>;
  baseline: null | {
    version_id: string; sha256: string; palabras: number | null;
    declaracion: null | { opciones: OpcionOrigen[]; ts: string; texto: string };
    declaration_verified: false;
    escaneo: Escaneo;
  };
  versiones: VersionSellada[];
  no_acredita: string[];
}

const enviarAwap = <T,>(ruta: string, cuerpo: unknown) =>
  fetch(BASE + ruta, { method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify(cuerpo) })
    .then((r) => conError<T>(r));

export const posicionAwap = (dir: string, doc?: string | null) =>
  pedir<{ posicion: Posicion | null }>(`/awap2/posicion?dir=${encodeURIComponent(dir)}${doc ? `&doc=${encodeURIComponent(doc)}` : ""}`)
    .then((d) => d.posicion);

/**
 * §4.6 — la profundidad de transformación de la vía B: tres niveles que NO se promedian y que no
 * entran en la puntuación. `disponible: false` trae el motivo: se mide contra el fichero que se
 * selló como V0, y si ya no está —o su huella no cuadra— no se mide y se dice.
 */
export interface Profundidad {
  disponible: boolean;
  motivo?: string;
  v0?: { fichero: string; sha256: string };
  literal?: { frases_v0: number; frases_vivas: number; supervivencia: number | null; reescrito: number | null; palabras_v0: number; palabras_ahora: number };
  estructural?: null | { escenas_anadidas: number; escenas_borradas: number; escenas_modificadas: number; parrafos_anadidos: number; parrafos_cortados: number; parrafos_reescritos: number; delta_palabras: number };
  motivo_estructural?: string | null;
  no_literal?: { declaradas: { ts: string; ambitos: Ambito[]; doc: string | null; nota: string | null; origen: string }[]; ambitos: Ambito[]; sin_declarar: boolean };
  capitulos?: { titulo: string; palabras_antes: number; palabras_ahora: number; supervivencia: number | null; estado: string }[];
}
export const profundidadAwap = (dir: string, doc?: string | null) =>
  pedir<{ profundidad: Profundidad }>(`/awap2/profundidad?dir=${encodeURIComponent(dir)}${doc ? `&doc=${encodeURIComponent(doc)}` : ""}`)
    .then((d) => d.profundidad);

export const escaneoProcedencia = (doc: string) =>
  pedir<{ escaneo: Escaneo; aviso: string }>(`/awap2/procedencia?doc=${encodeURIComponent(doc)}`);

export const entrarAwap = (c: { dir: string; titulo: string; autor: string; entry_point: EntryPoint; doc?: string | null }) =>
  enviarAwap<{ proyecto: unknown }>("/awap2/entrar", c);

export const declararAwap = (c: { dir: string; texto: string; opciones: OpcionOrigen[]; autor?: string }) =>
  enviarAwap<{ proyecto: unknown }>("/awap2/declarar", c);

export const sellarAwap = (c: { dir: string; doc: string }) =>
  enviarAwap<{ version: VersionSellada }>("/awap2/sellar", c);

/** El certificado PROVISIONAL de siete secciones: el motor lo guarda en la carpeta del libro. */
export interface CertificadoProvisional {
  ruta: string; huella: string; paginas: number; cuadrante: { id: string; nombre: string };
  has_version: string; pdf_base64: string;
}
export const certificadoAwap = (c: { dir: string; doc?: string | null }) =>
  enviarAwap<CertificadoProvisional>("/awap2/certificado", c);

/**
 * §8.3 — ANCLAR el certificado: es lo único que le quita el «PROVISIONAL».
 *
 * El token de la cuenta viaja al motor y el motor ancla la huella en verify.humanink.io. Solo se
 * emite el PDF definitivo si el anclaje se hizo de verdad: sin red no hay definitivo, porque un
 * anclaje es una fecha en un registro público o no es nada.
 */
export interface CertificadoAnclado extends CertificadoProvisional {
  anclado: string; url: string; repetido?: boolean;
}
export const anclarCertificado = (c: { dir: string; doc?: string | null; clerk: string }) =>
  enviarAwap<CertificadoAnclado>("/awap2/certificado/anclar", c);

// ── Modelos: qué modelo usa cada agente ────────────────────────────────────
export interface ModeloCatalogo {
  id: string;
  nombre: string;
  proveedor: string;
  contexto: number | null;
  entrada: number | null;   // USD por millón de tokens
  salida: number | null;
  /** Motor 1.6.7 (M-08): si se puede servir con retención cero. `null` = el catálogo no lo sabe. */
  zdr?: boolean | null;
  /** Motor 1.6.24: los niveles de esfuerzo que acepta y el que usa si no se le dice nada. */
  esfuerzos?: string[] | null;
  esfuerzoDefecto?: string | null;
}

/** La promesa de privacidad, escrita con precisión: se dice en Ajustes, en Modelos y en la web. */
export const PROMESA_PRIVACIDAD =
  "Tu manuscrito no sale de tu ordenador salvo el fragmento que envías al modelo, con tu propia llave, " +
  "y solo a proveedores que no guardan ni entrenan con él. Nosotros no lo vemos nunca.";
export interface ModelosEstado {
  defecto: string;
  grupos: Record<string, string>;      // grupo → nombre para el autor, en orden
  asignados: Record<string, string>;   // grupo → id de OpenRouter
  porAgente: Record<string, string>;   // colaborador → el modelo con el que trabajará, ya resuelto
  /** Motor 1.6.24: el esfuerzo elegido por grupo, el de la reescritura si no eliges y los niveles. */
  esfuerzos?: Record<string, string>;
  esfuerzoReescritura?: string;
  niveles?: string[];
}
export const modelosAsignados = () => pedir<ModelosEstado>("/modelos");
export const catalogoModelos = () =>
  pedir<{ modelos: ModeloCatalogo[]; ts: number | null; fuente: string; motivo?: string }>("/modelos/catalogo");
/** `null` devuelve el grupo al de por defecto. */
export const guardarModelos = (asignados: Record<string, string | null>) =>
  fetch(BASE + "/modelos", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ asignados }),
  }).then((r) => conError<ModelosEstado>(r));

/** El esfuerzo por grupo; `null` vuelve al del modelo (motor 1.6.24). */
export const guardarEsfuerzos = (esfuerzos: Record<string, string | null>) =>
  fetch(BASE + "/modelos", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ esfuerzos }),
  }).then((r) => conError<ModelosEstado>(r));

/**
 * EL INSTALADOR, DESDE DENTRO (R4.3).
 *
 * El manifiesto lo sirve el propio Studio (`public/releases/latest.json`), mismo origen: en
 * desarrollo lo da Vite, en producción Cloudflare Pages, y no hay CORS que negociar ni una ruta
 * más en el worker. Se versiona con el código, que es lo que convierte «publicar una versión» en
 * un commit en vez de en un `wrangler kv put` a mano.
 *
 * El motor sigue leyendo su aviso de versión de `verify.humanink.io/latest?product=studio`: KV
 * manda ahí para poder anunciar una versión retirada sin desplegar. Este fichero es el que el
 * worker usa de respaldo, así que los dos dicen lo mismo mientras no se toque KV.
 *
 * PENDIENTE, apuntado para que no sorprenda: el día que el motor sirva el Studio desde localhost
 * —hoy los lanzadores abren studio.humanink.io— esta ruta relativa apuntará al motor, que no tiene
 * el fichero, y la tarjeta desaparecerá en silencio. No vale pedirlo a studio.humanink.io desde
 * localhost: Pages sirve los estáticos sin CORS. Lo que toca entonces es que el motor sirva el
 * manifiesto junto al resto del Studio.
 */
export interface Descargas {
  version: string;
  publicado?: string;
  pkg?: string; pkgFirmado?: boolean;
  win?: string; winFirmado?: boolean; winZip?: string;
  /** La versión de Windows, cuando va por detrás de la de Mac (23-sep). */
  winVersion?: string;
  /** Y su fecha, cuando Windows sale otro día (2-oct: la 1.6.33 salió antes en Windows). */
  winPublicado?: string;
  notes?: string;
}

export async function descargas(): Promise<Descargas | null> {
  // Nunca tumba la pantalla: sin manifiesto, la tarjeta no se pinta. Es lo mismo que hace el aviso
  // de versión, y por la misma razón —no saber no es un error que el autor tenga que leer—.
  try {
    const r = await fetch("/releases/latest.json", { cache: "no-cache" });
    if (!r.ok) return null;
    const d = await r.json();
    return d && typeof d.version === "string" ? d : null;
  } catch {
    return null;
  }
}

/**
 * Qué sistema tiene delante, para ofrecerle SU fichero y no una lista.
 *
 * Se mira primero `userAgentData.platform`, que es lo único que Chrome no está congelando, y se
 * cae al `userAgent` de siempre. Si no se sabe, se devuelve null y la tarjeta enseña los dos:
 * adivinar mal y darle a alguien el instalador de otro sistema es peor que preguntar.
 */
export type Sistema = "mac" | "win" | null;

export function sistema(nav: { userAgentData?: { platform?: string }; userAgent?: string; platform?: string } = navigator): Sistema {
  const s = String(nav.userAgentData?.platform || nav.platform || nav.userAgent || "").toLowerCase();
  if (/mac|darwin|iphone|ipad/.test(s)) return "mac";
  if (/win/.test(s)) return "win";
  return null;
}

// ── LOS PROYECTOS ───────────────────────────────────────────────────────────
//
// Hasta hoy «el proyecto» era una ruta en el `localStorage` de este navegador: vaciar los datos del
// sitio, o entrar desde otro navegador, y el autor tenía que volver a buscar su libro. El registro
// pasa al motor, que es del equipo, y así el panel de Word puede saber en qué libro estás.
//
// Un proyecto es un NOMBRE y una CARPETA. El estado de cada libro —bitácora, AWAP, hilos— sigue
// viviendo en `<carpeta>/.humanink/`, así que adoptar una carpeta con un año de trabajo dentro no
// migra nada.
export interface Proyecto {
  id: string;
  nombre: string;
  carpeta: string;
  descripcion: string;
  creado: string;
  visto: string;
  autor: string | null;
  estilo: string | null;
  modelos: Record<string, string> | null;
  /** El manuscrito abierto, guardado en el motor (1.6.13): lo que sobrevive a una recarga. */
  documento?: string | null;
  /** Qué es el libro (M-01) y su género. Sin tipo, el informe no se lanza. */
  tipo?: TipoLibro | null;
  genero?: string;
  /** Cómo lee el motor ese género (1.6.29): la pantalla enseña esto, no el texto guardado. */
  genero_leido?: GeneroLeido | null;
  existe: boolean;
  /** `ok` · `no_esta` (disco desenchufado, carpeta movida) · `no_es_carpeta` */
  estado: "ok" | "no_esta" | "no_es_carpeta";
  /** Solo al crearlo: si la carpeta la hicimos nosotros o era suya de antes. */
  creada?: boolean;
  adoptado?: boolean;
}

/** Un no del motor con su código, para poder decir «esa carpeta ya es el proyecto X». */
export class FalloProyecto extends Error {
  constructor(mensaje: string, public codigo: string) { super(mensaje); }
}

async function conCuerpo<T>(ruta: string, metodo: string, cuerpo?: unknown): Promise<T> {
  const r = await fetch(BASE + ruta, {
    method: metodo,
    headers: cabeceras(cuerpo === undefined ? {} : { "content-type": "application/json" }),
    body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
  });
  if (r.status === 401) throw new SinSesion("El panel está cerrado.");
  const d = await r.json().catch(() => ({}));
  // Dos formas de error en el motor: `{mensaje, error: código}` (proyectos) y `{error: texto,
  // codigo}` (notas, vuelta). Enseñar la URL en vez del texto dejaba al autor sin saber qué hacer.
  if (!r.ok) throw new FalloProyecto(d.mensaje || (d.codigo ? textoDeError(d) : null) || `${ruta} respondió ${r.status}`, d.codigo || (typeof d.error === "string" ? d.error : null) || "no_valido");
  return d as T;
}

export const proyectos = () => pedir<{ activo: string | null; proyectos: Proyecto[] }>("/proyectos");
/** ¿El manuscrito tiene cambios sin decidir, o Word lo tiene abierto? (motor 1.6.14). `null` si no se sabe. */
export async function documentoPendiente(doc: string): Promise<{ marcas: number | null; abierto_en_word: boolean; indicaciones?: number } | null> {
  const r = await fetch(BASE + `/documento/pendiente?${new URLSearchParams({ doc })}`, { headers: cabeceras() }).catch(() => null);
  if (!r || !r.ok) return null;
  // `indicaciones` (motor 1.7): los sitios «[ESCRIBE AQUÍ…]» que siguen en el manuscrito sin el texto del autor.
  return (await r.json().catch(() => null)) as { marcas: number | null; abierto_en_word: boolean; indicaciones?: number } | null;
}
/** Guardar en el motor qué manuscrito tienes abierto. Un motor anterior no tiene la ruta: se calla. */
export const guardarDocumentoAbierto = (id: string, doc: string) =>
  fetch(BASE + "/proyectos/documento", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ id, doc }),
  }).then(() => undefined).catch(() => undefined);
export const crearProyecto = (d: { nombre: string; carpeta?: string; descripcion?: string }) =>
  conCuerpo<Proyecto>("/proyectos", "POST", d);
export const elegirProyecto = (id: string) => conCuerpo<Proyecto>("/proyectos/elegir", "POST", { id });
export const renombrarProyecto = (id: string, d: { nombre?: string; descripcion?: string }) =>
  conCuerpo<Proyecto>("/proyectos/nombre", "POST", { id, ...d });
/** La carpeta que estaba en el navegador, una sola vez. Si ya hay proyectos, no hace nada. */
export const adoptarProyecto = (carpeta: string) =>
  conCuerpo<{ proyecto: Proyecto | null }>("/proyectos/adoptar", "POST", { carpeta });
/** Saca del registro. NO borra ficheros: la respuesta lo confirma y la pantalla lo dice. */
export const olvidarProyecto = (id: string) =>
  conCuerpo<{ olvidado: string; carpeta: string; ficheros_intactos: boolean; activo: string | null }>(
    `/proyectos?${new URLSearchParams({ id })}`, "DELETE");

// ── LOS AUTORES ─────────────────────────────────────────────────────────────
//
// Una firma: el nombre con el que sale un libro, con sus seudónimos si los agrupa. Cada una tiene
// su carpeta y su perfil, y el perfil NO viaja al registro —es texto largo y confidencial—: se
// mira en el disco cada vez, así que si lo borras o lo rehaces, la pantalla lo sabe.
export interface Autor {
  id: string;
  nombre: string;
  seudonimos: string[];
  carpeta: string;
  /** `sin_perfil` · `completo` (hay perfil-autor.docx) · `sin_carpeta` (la carpeta ya no está) */
  perfil: { estado: "sin_perfil" | "completo" | "sin_carpeta"; docx: string | null; actualizado: string | null; versiones?: number };
  /** Solo al crearlo: si la carpeta la hicimos nosotros. */
  creada?: boolean;
  /** Lo que deja hacer con su texto (motor 1.7). Sin el campo, el motor es anterior. */
  preferencias?: PreferenciasEdicion;
}

export const autores = () => pedir<{ autores: Autor[] }>("/autores");
export const crearAutor = (d: { nombre: string; seudonimos?: string }) => conCuerpo<Autor>("/autores", "POST", d);
/** Guardar lo que el autor deja hacer (motor 1.7). Un motor anterior contesta 404: se dice que hay que actualizar. */
export async function preferirAutor(id: string, d: Partial<{ poda: boolean; estilo: boolean; texto_ia: boolean; intocables: string[] }>): Promise<Autor> {
  const r = await fetch(BASE + "/autores/preferencias", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ id, ...d }),
  });
  if (r.status === 401) throw new SinSesion("El panel está cerrado.");
  if (r.status === 404 && !(await r.clone().json().catch(() => null))?.error) {
    throw new Error("Para guardar tus preferencias hace falta el motor nuevo. Actualízalo desde Ajustes.");
  }
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.mensaje || `No se han podido guardar (${r.status}).`);
  return j as Autor;
}
/** Las preferencias de la firma del libro abierto, como las ve el plan. `null`: el motor no las tiene. */
export async function preferenciasDelLibro(dir: string, doc?: string | null): Promise<PreferenciasEdicion | null> {
  const q = new URLSearchParams(doc ? { dir, doc } : { dir });
  const r = await fetch(BASE + `/plan?${q}`, { headers: cabeceras() }).catch(() => null);
  if (!r) return null;
  const d = (await r.json().catch(() => ({}))) as { preferencias?: PreferenciasEdicion };
  return d.preferencias ?? null;
}
export const renombrarAutor = (id: string, d: { nombre?: string; seudonimos?: string }) =>
  conCuerpo<Autor>("/autores/nombre", "POST", { id, ...d });
/** Saca la firma del registro. NO borra su carpeta ni su perfil. */
export const olvidarAutor = (id: string) =>
  conCuerpo<{ olvidado: string; carpeta: string; ficheros_intactos: boolean }>(`/autores?${new URLSearchParams({ id })}`, "DELETE");
/** Proyectos II: qué firma lleva el libro. `null` deshace el vínculo. */
export const vincularProyecto = (id: string, d: { autor?: string | null }) =>
  conCuerpo<Proyecto>("/proyectos/vincular", "POST", { id, ...d });

// ── LAS FICHAS DE ESTILO (Estilos) ──────────────────────────────────────────
//
// Los diez ejes del Volumen 7. Ninguna medida se convierte en puntuación: las diez son JUICIOS con
// su frase, y las medidas van al lado como evidencia. «No lo sé» es una respuesta legítima y se
// pinta como tal — un 6 inventado sería peor que un hueco.
export interface EjeEstilo { n: number; id: string; nombre: string }
export interface PuntuacionEje { puntuacion: number | null; justificacion: string }
export interface Maestro { id: string; nombre: string; huella: Record<string, number>; provisional?: boolean }

export interface FichaEstilo {
  id: string;
  nombre: string;
  creada: string;
  muestras: { n: number; fichero: string; palabras: number; anadida: string; origen: string | null;
              medidas?: Record<string, never> | Record<string, unknown>; avisos?: string[] }[];
  puntuaciones: { fecha: string; origen: string; muestra: number | null; ejes: Record<string, PuntuacionEje> }[];
  fiabilidad: { muestras: number; veredicto: "sin_datos" | "una_muestra" | "estable" | "pasaje";
                ejesDispares: { id: string; min: number; max: number }[] };
}

export const bibliotecaEstilos = () =>
  pedir<{ ejes: EjeEstilo[]; biblioteca: Maestro[] }>("/estilos/biblioteca");
export const fichasDeEstilo = (autor: string) =>
  pedir<{ autor: string; fichas: FichaEstilo[] }>(`/estilos?${new URLSearchParams({ autor })}`);
export const crearFichaEstilo = (autor: string, nombre: string) =>
  conCuerpo<FichaEstilo>("/estilos", "POST", { autor, nombre });
export const anadirMuestraEstilo = (autor: string, ficha: string, texto: string, origen?: string) =>
  conCuerpo<{ ficha: FichaEstilo; muestra: { n: number; palabras: number }; igual: boolean }>(
    "/estilos/muestra", "POST", { autor, ficha, texto, origen });
/**
 * Rellenar la ficha con lo que YA dijo el informe de lectura del libro (T-36). No se puntúa dos
 * veces lo mismo: el lector juzgó el estilo y aquí se guarda donde el editor lo lee.
 */
export const fichaDesdeInforme = (autor: string, ficha: string, dir: string, doc?: string | null) =>
  conCuerpo<{ ficha: FichaEstilo; puntuacion: { origen: string; ejes: Record<string, PuntuacionEje> };
              referencias: { autor: string; por_que: string; parecido: string; perfil: "biblioteca" | "lector" }[] | null }>(
    "/estilos/del-informe", "POST", { autor, ficha, dir, doc });

/** Los seis tipos de obra, con sus dimensiones. Son la vara con la que se lee el libro (M-01, M-02). */
export const TIPOS_DE_OBRA: { id: TipoLibro; nombre: string; mira: string }[] = [
  { id: "novela", nombre: "Novela", mira: "Estructura · trama · personajes · punto de vista · ritmo · voz" },
  { id: "relatos", nombre: "Relatos", mira: "Unidad · arranques y cierres · personajes · variedad · ritmo · voz" },
  { id: "ensayo", nombre: "Ensayo · divulgación", mira: "Tesis · argumento · evidencia · claridad · coherencia · persuasión" },
  { id: "memorias", nombre: "Memorias", mira: "Arco del narrador · verdad · personas · escenas · estructura · voz" },
  { id: "manual", nombre: "Manual · práctico", mira: "Promesa · progresión · ejercicios · claridad · utilidad · estructura" },
  { id: "guion", nombre: "Guion", mira: "Estructura · escenas · personajes · diálogo · acción · formato" },
];

/**
 * Los géneros (27-sep, Elisa): la misma lista que `companion/generos.mjs` del motor. Con el género se
 * elige la lente con que se lee la estructura; «otro» lo describe el autor con sus palabras.
 */
export const GENEROS_DE_LIBRO: { id: string; nombre: string; lente: string | null }[] = [
  { id: "thriller", nombre: "Thriller y suspense", lente: "Trama (Save the Cat y actos)" },
  { id: "negra", nombre: "Novela negra", lente: "Trama (Save the Cat y actos)" },
  { id: "romantica", nombre: "Romántica", lente: "Trama (Save the Cat y actos)" },
  { id: "fantasia", nombre: "Fantasía y ciencia ficción de género", lente: "Trama (Save the Cat y actos)" },
  { id: "historica", nombre: "Histórica", lente: "Trama (Save the Cat y actos)" },
  { id: "terror", nombre: "Terror", lente: "Trama (Save the Cat y actos)" },
  { id: "literaria", nombre: "Literaria", lente: "Diseño propio de la obra" },
  { id: "visionaria", nombre: "Ficción visionaria y simbólica", lente: "Coral y acumulativa" },
  { id: "autoficcion", nombre: "Autoficción y memorias", lente: "Diseño propio de la obra" },
  { id: "otro", nombre: "Otro (lo describes tú)", lente: null },
];

/** Lo que el motor propone leyendo el principio del documento; `null` si no ha sabido decirlo (motor 1.6.29). */
export const proponerGenero = (doc: string) =>
  conCuerpo<{ genero: { id: string; descripcion: string | null; matiz: string | null } | null; dicho: string }>("/proyectos/genero/proponer", "POST", { doc });

/** Decir qué es el libro. La lista está cerrada en el motor: un tipo que no está no pisa el que había. */
export const clasificarProyecto = (id: string, d: { tipo?: TipoLibro; genero?: string }) =>
  conCuerpo<{ proyecto: Proyecto }>("/proyectos/tipo", "POST", { id, ...d }).then((r) => r.proyecto);

/** Lo que el motor propone leyendo el principio del documento. Propone; decide el autor. */
export const proponerTipo = (doc: string) =>
  conCuerpo<{ tipo: TipoLibro | null; dicho: string }>("/proyectos/tipo/proponer", "POST", { doc });
/**
 * El puente hasta Autores II: el chat deja `perfil-autor.docx` en la carpeta del proyecto abierto,
 * y esto lo COPIA a la carpeta de la firma. Nunca pisa uno que ya tuviera: si es distinto, `-2`.
 */
export const traerPerfil = (id: string, desde: string) =>
  conCuerpo<{ autor: Autor; traido: string; igual: boolean; renombrado: boolean }>("/autores/traer-perfil", "POST", { id, desde });

// ── MANUSCRITO I ─────────────────────────────────────────────────────────────
//
// El manuscrito, la biblia y la escaleta, y qué falta. El motor los busca con `siguienteBuild`
// (las tres formas: m01-v01, -bNN y capítulos) y con el nombre exacto de biblia/escaleta —antes
// esto se adivinaba en el propio Studio con `name.startsWith("biblia")`, que un
// "bibliagrafia.docx" también hacía saltar—. Un motor anterior a esta ruta no la tiene: se lee
// como "sin nada", nunca como un error que tape la pantalla.
export interface EstadoDoc { existe: boolean; ruta: string | null }
export interface EstadoManuscrito {
  disponible: boolean;
  motivo?: string;
  manuscrito?: EstadoDoc;
  biblia?: EstadoDoc;
  escaleta?: EstadoDoc;
  sugerencia?: { colaborador: "coach" | "ghostwriter"; texto: string } | null;
}
export const estadoManuscrito = (dir: string) =>
  pedir<EstadoManuscrito>(`/manuscrito/estado?${new URLSearchParams({ dir })}`);

// ── EL CHAT DE REESCRITURA (Agentes II) ────────────────────────────────────────────────────────
//
// Un hilo por proyecto: el autor habla con el editor, y el editor encarga al escritor fantasma, al
// coach o al corrector. Cada mensaje lleva `quien` —«editor», «ghostwriter», «coach»…— y, si fue un
// encargo, qué le pidió el editor. Un motor anterior a la 1.6.2 no tiene la ruta: 404.
export interface TurnoReescritura {
  role: "user" | "assistant";
  content: string;
  at?: number;
  consultas?: Consulta[];     // CH-4: lo que miró del libro (motor 1.6.29)
  propuestas?: Propuesta[];   // CH-4 fase B: las fichas del editor (motor 1.6.29)
  quien?: string;
  encargo?: string;
  encarga?: string[];
  fallo?: boolean;
  cierre?: boolean;   // T-28: el editor presenta lo que ha entregado su equipo (motor 1.6.4)
}

const leerReescritura = (dir: string) =>
  pedir<{ history?: TurnoReescritura[]; faltanEstilo?: string[] }>(`/reescritura?dir=${encodeURIComponent(dir)}`);

export const hiloReescritura = (dir: string) => leerReescritura(dir).then((d) => d.history ?? []);

/** El hilo y lo que le falta al editor para respetar tu voz (T-29). Sin el dato (motor anterior), `null`. */
export const hiloReescrituraConEstilo = (dir: string) =>
  leerReescritura(dir).then((d) => ({ hilo: d.history ?? [], faltanEstilo: Array.isArray(d.faltanEstilo) ? d.faltanEstilo : null }));

export async function reescribirEnEquipo(
  dir: string, texto: string, contexto: string[], turno: number, modelo?: string | null, libroEntero = false, doc: string | null = null,
): Promise<TurnoReescritura[]> {
  const idempotencia = await claveIdempotencia({ hilo: "reescritura", dir, turno, texto, modelo: modelo ?? null, libroEntero });
  const r = await fetch(BASE + `/reescritura?dir=${encodeURIComponent(dir)}`, {
    method: "POST",
    headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ text: texto, contextFiles: contexto, idempotencia, contextFull: libroEntero, ...(doc ? { doc } : {}), ...(modelo ? { model: modelo } : {}) }),
  });
  if (r.status === 404) throw new Error("El chat de reescritura necesita el motor 1.6.2. Actualízalo desde Ajustes.");
  if (!r.ok) await fallo(r);
  const d = (await r.json()) as { turnos?: TurnoReescritura[] };
  return d.turnos ?? [];
}

export async function olvidarReescritura(dir: string) {
  const r = await fetch(BASE + `/reescritura?dir=${encodeURIComponent(dir)}`, { method: "DELETE", headers: cabeceras() });
  if (!r.ok) await fallo(r);
}

// ── EL CAMINO (Fase 12) ────────────────────────────────────────────────────────────────────────
//
// El plan de reescritura del libro. Lo da `GET /plan` desde el motor 1.6.3; con un motor anterior
// la ruta no existe (404) y se devuelve "sin_ruta" para que la pantalla lo deduzca de los ficheros.
export interface PasadaPlan { n: number; titulo: string; estado: "pendiente" | "hecha" | "saltada" | string; capitulos?: number[] | string; build?: string | null; criterio_cierre?: string; delta_esperado?: number; propia?: boolean; editada?: boolean;
  /** Hecha, lo que dejó en el tablero (motor 1.6.30): su propia nota y las que ya existían y contestaba. */
  nota?: string | null; notas?: string[];
  /** El plan libre (motor 1.7): prioridad y la frase de por qué se hace. */
  prioridad?: "alta" | "media" | "baja" | string; porque?: string;
  /** Lo que dice el perfil del autor de esta pasada (motor 1.7): qué preferencia la para, y si en vez de
   *  texto nuevo deja indicaciones. `saltada_por: "preferencias"` cuando no la saltó él. */
  contra?: string[]; modo?: string; saltada_por?: string }

/** Un cambio del autor sobre una pasada (motor 1.6.5). Una pasada hecha no se cambia. */
export type CambioPasada =
  | { accion: "mover"; n: number; hacia: -1 | 1 }
  | { accion: "saltar" | "recuperar" | "quitar"; n: number }
  | { accion: "editar"; n: number; titulo?: string; capitulos?: string | number[]; criterio_cierre?: string }
  | { accion: "anadir"; titulo: string; capitulos: string | number[]; criterio_cierre?: string };

export async function editarPasada(dir: string, cambio: CambioPasada, doc?: string | null): Promise<PlanLibro> {
  const r = await fetch(BASE + "/plan", {
    method: "PATCH", headers: cabeceras({ "content-type": "application/json" }),
    // `doc`: el plan es del LIBRO (T-43). Un motor anterior a la 1.6.7 lo ignora.
    body: JSON.stringify({ dir, doc: doc ?? null, pasada: cambio }),
  });
  if (r.status === 409) throw new Error(textoDeError(await r.json().catch(() => ({}))) ?? "No se ha podido cambiar el plan.");
  if (!r.ok) await fallo(r);
  const d = (await r.json()) as { plan?: PlanLibro; editado?: boolean };
  // Un motor 1.6.4 contesta 200 con el plan SIN cambios (equipo rojo 17-sep): sin `editado`, no se hizo nada.
  if (!d.plan || !d.editado) throw new Error("Para cambiar pasadas hace falta el motor 1.6.5. Actualízalo desde Ajustes.");
  return d.plan;
}
export interface PlanLibro {
  aprobado: boolean;
  /** El manuscrito con el que se escribió: una pasada sobre otro sería otro libro. */
  manuscrito_base?: string | null;
  fecha?: string | null;
  areas: { nombre: string; pasadas: PasadaPlan[]; puerta?: string }[];
  zonas_intocables?: string[];
  /** Las que propuso el editor sin que el autor las declarara (motor 1.6.20): no cuentan hasta que el autor las marque. */
  zonas_propuestas?: string[];
  /** Las zonas las puso el autor (motor 1.6.20). Sin la marca, un plan anterior las tiene escritas por el editor. */
  zonas_autor?: boolean;
  fecha_aprobado?: string | null;
  documento?: string | null;
  recortado?: boolean;
  objetivo_palabras?: number;
  palabras_actuales?: number;
  /** `libre` (motor 1.7): las fases y pasadas que decidió el modelo; sin aprobar no se ejecuta. */
  forma?: string;
}
/** Preparar el plan: trabajo en segundo plano del motor 1.6.3. */
export async function prepararPlan(dir: string, doc: string, objetivo?: string, rehacer = false,
  decide: { zonas?: string[]; alcance?: string; libre?: boolean } = {}): Promise<Trabajo> {
  const r = await fetch(BASE + "/plan", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }),
    // `zonas` y `alcance` (25-sep): las preguntas de la skill del plan. Un motor anterior las ignora.
    // `libre` (3-oct): el plan del flujo, con las fases y pasadas que decida el modelo. Un motor anterior lo ignora
    // y prepara el de las diez pasadas.
    body: JSON.stringify({ dir, doc, objetivo: objetivo || null, rehacer, zonas: decide.zonas ?? [], alcance: decide.alcance || null, ...(decide.libre ? { libre: true } : {}) }),
  });
  if (!r.ok) await fallo(r);
  return ((await r.json()) as { trabajo: Trabajo }).trabajo;
}
/** Aprobar el plan o cambiar sus zonas intocables. */
export async function actualizarPlan(dir: string, cambios: { aprobado?: boolean; zonas_intocables?: string[] }, doc?: string | null): Promise<PlanLibro> {
  const r = await fetch(BASE + "/plan", {
    method: "PATCH", headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ dir, doc: doc ?? null, ...cambios }),
  });
  if (!r.ok) await fallo(r);
  return ((await r.json()) as { plan: PlanLibro }).plan;
}
export async function planDelLibro(dir: string, doc?: string | null): Promise<PlanLibro | null | "sin_ruta"> {
  const q = new URLSearchParams(doc ? { dir, doc } : { dir });
  const r = await fetch(BASE + `/plan?${q}`, { headers: cabeceras() });
  if (r.status === 404) {
    const d = await r.json().catch(() => ({}));
    return (d as { error?: string }).error === "sin_plan" ? null : "sin_ruta";
  }
  if (!r.ok) throw new Error(`/plan respondió ${r.status}`);
  return ((await r.json()) as { plan: PlanLibro | null }).plan;
}

/**
 * Cuánto tardaría preparar el plan de este libro (Rais, 4-oct: «una estimación del total de minutos»).
 * `null` con un motor que no lo estima (ignora `estimar`) o si no se puede leer el libro.
 */
export async function estimacionDelPlan(dir: string, doc: string): Promise<{ minutos: number } | null> {
  const r = await fetch(BASE + `/plan?${new URLSearchParams({ dir, doc, estimar: "1" })}`, { headers: cabeceras() });
  // Sin plan todavía el motor contesta 404 «sin_plan», y la estimación viene igual en el cuerpo.
  const d = (await r.json().catch(() => ({}))) as { estimacion?: { minutos?: number } };
  return typeof d.estimacion?.minutos === "number" ? { minutos: d.estimacion.minutos } : null;
}

// ── EL DIAGNÓSTICO ENTERO (M-2, motor 1.7) ─────────────────────────────────────────────────────
export interface DiagnosticoLibro { doc: string; fecha: string; ruta: string; faltan: string[];
  piezas: { tipo: string; titulo: string; nota: number | null; resumen: string | null; hecho: boolean; recortado?: boolean }[] }
/**
 * El Word del diagnóstico de este libro. `undefined`: el motor es anterior y no tiene la ruta (contesta
 * 404). Cualquier OTRO fallo —el motor reiniciándose, la red local— LANZA: no es «motor anterior», y
 * tratarlo así mandaba la pantalla por el camino caro, con los cuatro informes a la vez (equipo rojo, 3-oct).
 */
export async function diagnosticoDelLibro(dir: string, doc: string): Promise<DiagnosticoLibro | null | undefined> {
  const r = await fetch(BASE + `/diagnostico?${new URLSearchParams({ dir, doc })}`, { headers: cabeceras() });
  if (r.status === 404) return undefined;
  if (!r.ok) throw new Error(`/diagnostico respondió ${r.status}`);
  const d = (await r.json()) as { diagnostico?: DiagnosticoLibro | null; tipos?: string[] };
  if (!Array.isArray(d.tipos)) throw new Error("/diagnostico contestó algo que no se entiende");
  return d.diagnostico ?? null;
}
/** Pedir el diagnóstico entero: lo que falta de los informes de una lectura, de uno en uno, y su Word. */
export async function pedirDiagnostico(dir: string, doc: string): Promise<Trabajo> {
  const r = await fetch(BASE + "/diagnostico", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ dir, doc }),
  });
  if (!r.ok) await fallo(r);
  return ((await r.json()) as { trabajo: Trabajo }).trabajo;
}

// ── LOS INFORMES DEL DIAGNÓSTICO (R-14, motor 1.6.3) ───────────────────────────────────────────
export interface FichaInforme { tipo: string; titulo: string; nota: number | null; resumen: string | null; fecha: string; version: string; ruta: string | null; recortado?: boolean }
/** `null` si el motor es anterior a la 1.6.3 (no tiene la ruta). */
export async function informesDelLibro(dir: string): Promise<Record<string, FichaInforme> | null> {
  const r = await fetch(BASE + `/informes?dir=${encodeURIComponent(dir)}`, { headers: cabeceras() });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`/informes respondió ${r.status}`);
  const d = (await r.json()) as { informes: Record<string, FichaInforme>; tipos?: string[] };
  tiposDeInforme = d.tipos ?? null;
  return d.informes;
}
/**
 * Los informes que sabe hacer el motor instalado (desde la 1.6.3 lo dice `GET /informes`). La web se
 * publica antes de que el autor instale el motor: una fila nueva solo se enseña si su motor la tiene
 * (equipo rojo, 24-sep: «Pedirlo» en Adecuación al mercado con un motor 1.6.16 daba «Ese informe no existe»).
 */
export let tiposDeInforme: string[] | null = null;
export async function pedirInforme(tipo: string, dir: string, doc: string, rehacer = false): Promise<Trabajo> {
  const r = await fetch(BASE + `/informes/${tipo}`, {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }),
    // `rehacer` (25-sep): pedir otra vez es otra petición; sin él, en diez minutos volvía el de antes.
    body: JSON.stringify({ dir, doc, rehacer }),
  });
  if (!r.ok) await fallo(r);
  return ((await r.json()) as { trabajo: Trabajo }).trabajo;
}

/**
 * Traer al proyecto un manuscrito que vive fuera (otro disco, Descargas): el motor lo COPIA dentro
 * con el nombre versionado que toque. El original no se toca (motor 1.6.10).
 */
export async function traerAlProyecto(dir: string, origen: string): Promise<{ ruta: string; nombre: string; marcas: number | null }> {
  const r = await fetch(BASE + "/proyectos/traer", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ dir, origen }),
  });
  if (r.status === 404) throw new Error("Para traerlo solo hace falta actualizar el motor a la 1.6.10 desde Ajustes.");
  if (!r.ok) await fallo(r);
  return (await r.json()) as { ruta: string; nombre: string; marcas: number | null };
}

// ── UNA CARPETA POR LIBRO (R0-7, motor 1.6.30) ───────────────────────────────────────────────
export interface LibroDeCarpeta {
  libro: string; nombre: string; ficheros: string[]; versiones: number; ultimo: string;
  /** El otro proyecto que ya tiene este libro (separado antes, o que ya existía). */
  enProyecto?: { id: string; nombre: string; carpeta: string };
}
/** Los libros de la carpeta del proyecto. `null` con un motor anterior (no lo sabe decir): no se avisa. */
export async function librosDeCarpeta(dir: string): Promise<{ libros: LibroDeCarpeta[]; sueltos: string[] } | null> {
  const r = await fetch(BASE + `/proyectos/libros?dir=${encodeURIComponent(dir)}`, { headers: cabeceras() });
  if (r.status === 404) return null;
  if (!r.ok) await fallo(r);
  return (await r.json()) as { libros: LibroDeCarpeta[]; sueltos: string[] };
}
export interface Separado { proyecto: Proyecto; libro: string; copiados: string[]; documentos: number; atras: string[] }
/** Separar un libro en su propio proyecto: se COPIA; en la carpeta de origen no se mueve ni se borra nada. */
export async function separarLibro(dir: string, libro: string, nombre?: string): Promise<Separado> {
  const r = await fetch(BASE + "/proyectos/separar", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ dir, libro, nombre }),
  });
  if (r.status === 404) throw new Error("Para separarlo hace falta actualizar el motor desde Ajustes.");
  if (!r.ok) await fallo(r);
  return (await r.json()) as Separado;
}

// ── EL GASTO, OPERACIÓN A OPERACIÓN (motor 1.6.12) ─────────────────────────────────────────────
export interface GastoSuma { ops: number; llamadas: number; entrada: number; salida: number; /** motor 1.6.24: parte de la salida que fue razonamiento */ razonamiento?: number; cacheLeida: number; cacheEscrita: number; coste: number; ms: number }
export interface GastoLinea extends GastoSuma { id: string; ts: string; op: string; agente: string | null; modelo: string | null; titulo: string | null; doc: string | null; estado: string }
export interface Gasto {
  total: GastoSuma;
  porOp: Record<string, GastoSuma>;
  porAgente: Record<string, GastoSuma>;
  porModelo: Record<string, GastoSuma>;
  operaciones: GastoLinea[];
}
export async function gasto(dir?: string | null): Promise<Gasto> {
  const r = await fetch(BASE + `/gasto${dir ? `?dir=${encodeURIComponent(dir)}` : ""}`, { headers: cabeceras() });
  if (r.status === 404) throw new Error("Para ver el gasto por operación hace falta el motor 1.6.12. Actualízalo desde Ajustes.");
  if (!r.ok) await fallo(r);
  return (await r.json()) as Gasto;
}

/** Qué informes faltan para poder pedir el plan (motor 1.6.8). `null`: el motor no lo dice (anterior). */
export async function faltanParaElPlan(dir: string, doc?: string | null): Promise<{ id: string; titulo: string }[] | null> {
  const q = new URLSearchParams(doc ? { dir, doc } : { dir });
  const r = await fetch(BASE + `/plan?${q}`, { headers: cabeceras() }).catch(() => null);
  if (!r) return null;
  const d = (await r.json().catch(() => ({}))) as { faltan?: { id: string; titulo: string }[] };
  return Array.isArray(d.faltan) ? d.faltan : null;
}

/**
 * El informe de lectura COMPARADO (motor 1.6.8). Solo se puede después de reescribir y volver a
 * leer: el motor dice si se puede y, si no, por qué. `null`: el motor no tiene la ruta.
 */
export interface Comparable { posible: boolean; motivo: string | null; actual: string | null; anterior: string | null }
export async function comparado(dir: string, doc: string): Promise<Comparable | null> {
  const r = await fetch(BASE + `/informes/comparado?${new URLSearchParams({ dir, doc })}`, { headers: cabeceras() }).catch(() => null);
  if (!r || !r.ok) return null;
  const d = (await r.json().catch(() => null)) as Comparable | null;
  return d && typeof d.posible === "boolean" ? d : null;
}
export async function pedirComparado(dir: string, doc: string): Promise<Trabajo> {
  const r = await fetch(BASE + "/informes/comparado", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ dir, doc }),
  });
  if (!r.ok) await fallo(r);
  return ((await r.json()) as { trabajo: Trabajo }).trabajo;
}

/** El registro de la reescritura, en Word (motor 1.6.8). No gasta: sale de lo que ya está guardado. */
export async function hacerRegistro(dir: string, doc: string): Promise<{ ruta: string; versiones: number }> {
  const r = await fetch(BASE + "/registro", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ dir, doc }),
  });
  if (r.status === 404) throw new Error("Para el registro hace falta el motor 1.6.8. Actualízalo desde Ajustes.");
  if (!r.ok) await fallo(r);
  return (await r.json()) as { ruta: string; versiones: number };
}

/** Ejecutar la siguiente pasada del plan (motor 1.6.3). Los 409 traen `codigo`: marcas_pendientes, abierto_en_word… */
export class PuertaPasada extends Error { constructor(m: string, public codigo: string, public ultima?: string) { super(m); } }
/**
 * Ejecutar una pasada. Con `notas`, hace exactamente las que el autor ha puesto en ella y no hace
 * falta plan (M-18); con `n`, la pasada del plan; sin nada, la que toca.
 */
export async function ejecutarPasada(dir: string, doc: string, n: number | null = null, notas: string[] | null = null): Promise<{ trabajo: Trabajo; pasada: number; origen: string; salida: string }> {
  const cuerpo = notas?.length ? { dir, doc, notas } : n != null ? { dir, doc, n } : { dir, doc };
  const r = await fetch(BASE + "/pasada", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify(cuerpo),
  });
  if (r.status === 409 || r.status === 503) {
    const d = (await r.json().catch(() => ({}))) as { error?: string; codigo?: string; ultima?: string };
    // `ultima`: la versión buena de la carpeta, para poder ofrecer el cambio con un botón.
    throw new PuertaPasada(textoDeError(d) ?? "No se puede lanzar la pasada.", d.codigo ?? "puerta", d.ultima);
  }
  if (!r.ok) await fallo(r);
  return r.json();
}

// ── LAS ESCENAS DEL LIBRO (M-3, 3-oct-2026): el primer paso del plan ─────────────────────────────

/** Una escena, como la ve la pantalla: su sitio, su tamaño y por qué empieza ahí. El texto no viaja. */
export interface EscenaDelLibro {
  n: number; capitulo: number; capitulo_titulo: string | null; titulo: string;
  desde: number; hasta: number; palabras: number; por: string; motivo: string | null; arranque: string;
}
/** El libro partido en escenas. `fiable: false` = todavía no está partido (una escena por capítulo, de momento). */
export interface MapaDeEscenas {
  escenas: EscenaDelLibro[]; fuente: string | null; fiable: boolean; motivo: string | null; guardado: boolean;
  /** Los cortes guardados que ya no aparecen en el texto de hoy (motor con M-3). Los que solo se han corrido no se cuentan aquí: siguen a su texto. */
  perdidos?: { arranque: string | null; cap: string | null }[];
  /** Los capítulos que la lectura no pudo leer: cada uno sigue siendo una sola escena. Volver a dividir lee solo esos. */
  sin_leer?: string[];
}
/** Las escenas del libro, sin gastar. `undefined` = el motor no sabe de escenas (muy anterior). */
export async function escenasDelLibro(dir: string, doc: string): Promise<MapaDeEscenas | undefined> {
  const r = await fetch(BASE + "/escenas", { method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ dir, doc }) });
  if (r.status === 404) return undefined;
  if (!r.ok) await fallo(r);
  const d = (await r.json()) as MapaDeEscenas;
  return Array.isArray(d?.escenas) ? d : undefined;
}
/**
 * Dividir el libro en escenas, en segundo plano. Devuelve el trabajo; `"hecho"` si ya estaba partido
 * (no se llama a nadie); `undefined` si el motor todavía no lo hace desde aquí (es anterior).
 */
export async function dividirEnEscenas(dir: string, doc: string): Promise<Trabajo | "hecho" | undefined> {
  const r = await fetch(BASE + "/escenas/mapa", { method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ dir, doc }) });
  if (r.status === 404) return undefined;
  if (!r.ok) await fallo(r);
  const d = (await r.json()) as { trabajo?: Trabajo; hecho?: boolean };
  return d.trabajo ?? (d.hecho ? "hecho" : undefined);
}
/** Unir una escena con la anterior (`i` = su primera línea) o partir por la línea `i`. Manda lo que el autor toca. */
export const corregirEscenas = (dir: string, doc: string, cambio: { accion: "unir" | "partir"; i: number }) =>
  conCuerpo<{ cortes: { i: number }[]; fuente: string }>("/escenas/cortes", "PUT", { dir, doc, ...cambio });
/** Las líneas de una escena, para elegir por dónde partirla. `null`: el motor no lo da (es anterior). */
export async function lineasDeEscena(dir: string, doc: string, n: number): Promise<{ i: number; texto: string }[] | null> {
  const r = await fetch(BASE + `/escenas/lineas?${new URLSearchParams({ dir, doc, n: String(n) })}`, { headers: cabeceras() });
  if (!r.ok) return null;
  const d = (await r.json()) as { lineas?: { i: number; texto: string }[] };
  return Array.isArray(d.lineas) ? d.lineas : null;
}

/** Una pasada dentro de la fase que toca: lo que va a mandar al modelo, contado por el motor. */
export interface PasadaDeFase {
  n: number; titulo: string; prioridad?: string; modo?: string;
  palabras: number; llamadas: number; gratis: number; perdidas: number;
  por: "operaciones" | "capitulos"; capitulos: { n: number; titulo: string }[];
  /** Una pasada por capítulos que no encuentra los suyos en el manuscrito: no se puede hacer. */
  sin_capitulos?: boolean;
}
/**
 * La fase que toca, antes de pulsar (M-5). `fase: null` = no toca ninguna, y `codigo` dice por qué
 * (`sin_plan`, `sin_pasadas`, `registro_ilegible`). `fases` son todas, con lo que llevan hecho.
 */
export interface FasePrevia {
  fase: number | null; indice?: number; de?: number; area?: string; aprobado: boolean;
  codigo?: string; error?: string;
  pasadas?: PasadaDeFase[]; palabras?: number; llamadas?: number; necesita_llave?: boolean;
  /** Por qué no se puede lanzar tal como está (`capitulos`: alguna pasada no encuentra los suyos). */
  no_se_puede?: string;
  fases: { n: number; indice: number; nombre: string; pasadas: number; hechas: number; quitadas: number }[];
}
/**
 * Qué fase toca y cuánto va a mandar al modelo, sin gastar ni escribir.
 * `undefined` = el motor no sabe hacer una fase entera (es anterior): la pantalla sigue pasada a
 * pasada, como siempre. Un motor que contesta otra cosa cuenta igual: ante la duda, el camino viejo.
 */
export async function faseDelLibro(dir: string, doc: string, n: number | null = null): Promise<FasePrevia | undefined> {
  const r = await fetch(BASE + `/fase?${new URLSearchParams({ dir, doc, ...(n != null ? { n: String(n) } : {}) })}`, { headers: cabeceras() });
  if (r.status === 404) return undefined;
  if (!r.ok) throw new Error(`/fase respondió ${r.status}`);
  const d = (await r.json()) as FasePrevia;
  return Array.isArray(d?.fases) ? d : undefined;
}
/** Hacer la fase entera: sus pasadas, seguidas, y UNA versión. Mismas puertas que la pasada. */
export async function ejecutarFase(dir: string, doc: string, n: number | null = null): Promise<{ trabajo: Trabajo; fase: number; area: string; pasadas: number[]; salida: string }> {
  const r = await fetch(BASE + "/fase", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify(n != null ? { dir, doc, n } : { dir, doc }),
  });
  if (r.status === 409 || r.status === 503) {
    const d = (await r.json().catch(() => ({}))) as { error?: string; codigo?: string; ultima?: string };
    throw new PuertaPasada(textoDeError(d) ?? "No se puede lanzar la fase.", d.codigo ?? "puerta", d.ultima);
  }
  if (r.status === 404) throw new PuertaPasada("Tu motor hace las pasadas de una en una. Actualízalo desde Ajustes para hacer la fase entera.", "sin_fase");
  if (!r.ok) await fallo(r);
  return r.json();
}

/** Corregir el manuscrito entero en segundo plano (motor 1.6.3). Mismas puertas que la pasada. */
/** Resultado de una maquetación (motor 1.6.19). */
export interface Maquetacion {
  pdf: string | null; epub: string; html: string; paginas: number | null;
  lomo: { pulgadas: number; mm: number } | null; papel: string; tamano: string; capitulos: number; avisos: string[];
  partes?: number; titulo?: string; autor?: string;
}
/** Maquetar el libro: PDF de imprenta y EPUB, en segundo plano. `null` = el motor aún no sabe (anterior a la 1.6.19). */
export async function maquetar(dir: string, doc: string, opciones: { tamano: string; papel: string; titulo?: string; autor?: string }): Promise<{ trabajo: Trabajo } | null> {
  const r = await fetch(BASE + "/maquetar", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ dir, doc, ...opciones }),
  });
  if (r.status === 404) return null;
  if (!r.ok) await fallo(r);
  return r.json();
}
/** Un encargo: un entregable de una skill con su oficio y su modo (motor 1.6.20). */
export interface Encargo {
  id: string; etapa: string; agente: string; oficio: string; titulo: string; descripcion: string;
  pide: { campo: string; etiqueta: string; ayuda: string; obligatorio: boolean; largo?: boolean; valor?: string }[];
  limite: string | null; fichero: string; caracteres: number; tiene: string[]; faltan: string[];
  /** Además del Word deja una hoja de Excel (motor 1.7: la de seguimiento de editoriales). */
  excel?: boolean;
  hecho: { fecha: string; ruta: string; version: string | null; usados?: string[]; faltan?: string[]; excel?: string } | null;
}
/** Los encargos, con lo que viajaría cada uno. `null` = el motor aún no los tiene (anterior a la 1.6.20). */
export async function encargos(dir: string, doc: string | null): Promise<Encargo[] | null> {
  const q = new URLSearchParams({ dir, ...(doc ? { doc } : {}) });
  const r = await fetch(BASE + "/encargos?" + q, { headers: cabeceras() });
  if (r.status === 404) return null;
  if (!r.ok) await fallo(r);
  return ((await r.json()) as { encargos: Encargo[] }).encargos;
}
export async function pedirEncargo(id: string, dir: string, doc: string | null, datos: Record<string, string> = {}, rehacer = false): Promise<Trabajo> {
  const r = await fetch(BASE + `/encargos/${encodeURIComponent(id)}`, {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ dir, doc, datos, rehacer }),
  });
  if (!r.ok) await fallo(r);
  return ((await r.json()) as { trabajo: Trabajo }).trabajo;
}
/** La cubierta de tapa blanda para KDP (motor 1.6.22): lo que el motor sabe para rellenar el formulario. */
export interface DatosCubierta {
  disponible: boolean;
  maquetacion: { paginas: number; papel: string; tamano: string; lomo: { mm: number; pulgadas: number } | null } | null;
  titulo: string; autor: string;
  contraportada: { versiones: Record<string, string>; recomendada: string | null } | null;
  bio: string | null; bioDe: string | null;
  hecho: ResultadoCubierta | null;
}
export interface ResultadoCubierta {
  pdf: string; png: string; prueba: string; portada_ppp: number | null; paginas: number; papel: string; tamano: string;
  medidas: { ancho_in: number; alto_in: number; ancho_mm: number; alto_mm: number; lomo_mm: number; ppp: number };
}
export interface PortadaSubida { ruta: string; ancho: number; alto: number; tipo: string; ppp: number; anchoPara300: number }
/** `null` = el motor aún no monta cubiertas (anterior a la 1.6.22). */
export async function datosCubierta(dir: string, doc: string | null): Promise<DatosCubierta | null> {
  const q = new URLSearchParams({ dir, ...(doc ? { doc } : {}) });
  const r = await fetch(BASE + "/portada/wrap?" + q, { headers: cabeceras() });
  if (r.status === 404) return null;
  if (!r.ok) await fallo(r);
  return r.json();
}
export async function subirPortada(dir: string, fichero: File, tamano = "6x9"): Promise<PortadaSubida> {
  const bytes = new Uint8Array(await fichero.arrayBuffer());
  let bin = "";
  for (let i = 0; i < bytes.length; i += 8192) bin += String.fromCharCode(...bytes.subarray(i, i + 8192));
  const r = await fetch(BASE + "/portada/imagen", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }),
    body: JSON.stringify({ dir, nombre: fichero.name, base64: btoa(bin), tamano }),
  });
  if (!r.ok) await fallo(r);
  return r.json();
}
export async function montarCubierta(datos: { dir: string; doc: string | null; portada: string; contraportada: string; bio: string; titulo: string; autor: string; titular?: string }): Promise<Trabajo> {
  const r = await fetch(BASE + "/portada/wrap", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify(datos),
  });
  if (!r.ok) await fallo(r);
  return ((await r.json()) as { trabajo: Trabajo }).trabajo;
}
/** Una imagen de la carpeta del libro, para enseñarla (lleva la sesión: un <img src> no podría). */
export async function imagenDelLibro(dir: string, ruta: string): Promise<string | null> {
  const r = await fetch(BASE + "/portada/imagen?" + new URLSearchParams({ dir, ruta }), { headers: cabeceras() }).catch(() => null);
  if (!r?.ok) return null;
  return URL.createObjectURL(await r.blob());
}
/** Enseñar un fichero en el Finder o el Explorador (sin abrirlo). */
export async function mostrarEnCarpeta(ruta: string): Promise<boolean> {
  return (await mostrarEnCarpetaCon(ruta)) === "ok";
}
/** Lo mismo, diciendo POR QUÉ no se pudo: no está, el motor es anterior a la ruta, o no contesta. */
export async function mostrarEnCarpetaCon(ruta: string): Promise<Mostrado> {
  const r = await fetch(BASE + "/mostrar", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ ruta }),
  }).catch(() => null);
  if (!r) return "sin_motor";
  if (r.ok) return "ok";
  if (r.status !== 404) return "sin_motor";
  // Los dos 404 del motor: el de la ruta («Ese fichero ya no está») y el de un motor que no la tiene.
  const d = (await r.json().catch(() => ({}))) as { error?: string };
  return /ya no está/.test(d.error ?? "") ? "no_esta" : "motor_viejo";
}

export async function corregirManuscrito(dir: string, doc: string): Promise<{ trabajo: Trabajo; salida: string }> {
  const r = await fetch(BASE + "/correccion", {
    method: "POST", headers: cabeceras({ "content-type": "application/json" }), body: JSON.stringify({ dir, doc }),
  });
  if (r.status === 409 || r.status === 503) {
    const d = (await r.json().catch(() => ({}))) as { error?: string; codigo?: string };
    throw new PuertaPasada(textoDeError(d) ?? "No se puede lanzar la corrección.", d.codigo ?? "puerta");
  }
  if (!r.ok) await fallo(r);
  return r.json();
}
